import { useEffect, useRef, useState } from 'react';
import { AppShell } from '../../components/AppShell';
import { Skeleton, SkeletonLine } from '../../components/Skeleton';
import {
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../design';
import { ApiCallError } from '../../lib/api';
import { useMe, useModuleEnabled } from '../../lib/auth';
import { useCogsReplanState } from '../../lib/finance-summary';
import {
  accountingTreatmentLabel,
  eligibleWarehouseCharges,
  formatWarehouseCents,
  formatWarehouseQuantityMillis,
  groupWarehouseCharges,
  humanizeWarehouseBlocker,
  humanizeWarehouseBasis,
  humanizeWarehouseSource,
  isWarehouseServiceSupported,
  invoiceStatusLabel,
  parseWarehouseQuantityMillis,
  resolveWarehouseFacility,
  useAssignShipmentFacility,
  useCreateShipmentBox,
  useCreateWarehouseWorkCharge,
  useCreateWarehouseInvoice,
  useFinalizeShipmentBox,
  useReplaceShipmentBoxContents,
  useWarehouseBillingShipments,
  useWarehouseFacilities,
  useWarehouseIdempotencyKey,
  useWarehouseShipmentBoxes,
  useWarehouseServices,
  useWarehouseWorkCharges,
  useWarehouseInvoices,
  useWarehouseInvoice,
  useUpdateWarehouseInvoiceDraft,
  useSubmitWarehouseInvoice,
  useTransitionWarehouseInvoice,
  useReverseWarehouseWorkCharge,
  useRecordWarehouseInvoicePayment,
  useReverseWarehouseInvoicePayment,
} from '../../lib/warehouse';
import type {
  WarehouseShipment,
  WarehouseShipmentBox,
  WarehouseShipmentLine,
  WarehouseInvoiceAppendixPresentation,
  WarehouseInvoiceDetailResponse,
  WarehouseInvoiceSummary,
  WarehouseInvoicePayment,
  WarehouseWorkChargeResponse,
} from '@sourcito/schemas';

export type WarehouseBillingSection = 'packing' | 'charges' | 'invoices';

const SECTION_COPY: Record<WarehouseBillingSection, { title: string; description: string }> = {
  packing: {
    title: 'Amazon shipment packing',
    description:
      'For shipments already created in Seller Central, prepare their boxes for the next work step.',
  },
  charges: {
    title: 'Work charges',
    description: 'Record warehouse work and materials against their operational source.',
  },
  invoices: {
    title: 'Invoices',
    description: 'Review warehouse invoice drafts and their current seller-facing state.',
  },
};

function hasWarehouseRole(role: string | undefined): boolean {
  return role === 'warehouse' || role === 'owner' || role === 'operator';
}

function isUnassignedWarehouseError(error: unknown): boolean {
  if (!(error instanceof ApiCallError) || error.status !== 403) return false;
  const details = error.details;
  return (
    details !== null &&
    typeof details === 'object' &&
    'error' in details &&
    details.error !== null &&
    typeof details.error === 'object' &&
    'code' in details.error &&
    details.error.code === 'WAREHOUSE_NOT_ASSIGNED'
  );
}

function PageFrame({
  children,
  announcement = '',
}: {
  children: React.ReactNode;
  announcement?: string;
}) {
  return (
    <AppShell>
      <main className="max-w-5xl mx-auto px-6 sm:px-8 py-8 space-y-6">
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        {children}
      </main>
    </AppShell>
  );
}

type PackingBox = WarehouseShipmentBox;
type PackingLine = WarehouseShipmentLine;

export function totalBoxedUnits(boxes: PackingBox[], shipmentLineId: string): number {
  return boxes.reduce(
    (total, box) =>
      total +
      box.contents
        .filter((content) => content.shipmentLineId === shipmentLineId)
        .reduce((sum, content) => sum + content.listingUnits, 0),
    0
  );
}

export function currentBoxUnits(box: PackingBox, shipmentLineId: string): number {
  return box.contents
    .filter((content) => content.shipmentLineId === shipmentLineId)
    .reduce((sum, content) => sum + content.listingUnits, 0);
}

export function maxReplacementUnits(
  line: PackingLine,
  boxes: PackingBox[],
  box: PackingBox,
  shipmentLineId: string
): number {
  return Math.max(
    0,
    line.listingUnits -
      (totalBoxedUnits(boxes, shipmentLineId) - currentBoxUnits(box, shipmentLineId))
  );
}

export function remainingShipmentUnits(line: PackingLine, boxes: PackingBox[]): number {
  return Math.max(0, line.listingUnits - totalBoxedUnits(boxes, line.id));
}

function shipmentLabel(shipment: WarehouseShipment): string {
  return shipment.name?.trim() || shipment.shipmentId;
}

function shipmentStatusLabel(status: WarehouseShipment['status']): string {
  switch (status) {
    case 'preparing':
      return 'Preparing';
    case 'shipped':
      return 'Shipped';
    case 'receiving':
      return 'Receiving';
    case 'closed':
      return 'Closed';
  }
}

function packingErrorMessage(error: unknown): string {
  if (error instanceof ApiCallError) {
    const details = error.details;
    const detailsCode =
      details &&
      typeof details === 'object' &&
      'error' in details &&
      details.error &&
      typeof details.error === 'object' &&
      'code' in details.error &&
      typeof details.error.code === 'string'
        ? details.error.code
        : null;
    const match = detailsCode ?? error.message.match(/[A-Z][A-Z0-9_]+/)?.[0];
    if (match) return humanizeWarehouseBlocker(match);
    return error.message;
  }
  return error instanceof Error ? error.message : 'Packing update failed. Try again.';
}

function safePositiveInteger(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) return null;
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

function finalizedAtLabel(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Finalized time unavailable';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    date
  );
}

function shipmentSummary(shipment: WarehouseShipment): string {
  const listingUnits = shipment.lines.reduce((sum, line) => sum + line.listingUnits, 0);
  return `${shipment.lines.length} SKU${shipment.lines.length === 1 ? '' : 's'} · ${listingUnits} listing units`;
}

type ChargeResponse = WarehouseWorkChargeResponse;
type ChargeSourceKind = 'shipment' | 'shipment_line' | 'box';

function chargeCode(error: unknown): string | null {
  if (!(error instanceof ApiCallError)) return null;
  const details = error.details;
  if (details && typeof details === 'object' && 'error' in details) {
    const nested = details.error;
    if (nested && typeof nested === 'object' && 'code' in nested) {
      const code = nested.code;
      if (typeof code === 'string') return code;
    }
  }
  return error.message.match(/\b[A-Z][A-Z0-9_]+\b/)?.[0] ?? null;
}

function isPersistedBlockedCharge(error: unknown): boolean {
  if (!(error instanceof ApiCallError) || error.status !== 422) return false;
  const details = error.details;
  if (!details || typeof details !== 'object' || !('error' in details)) return false;
  const nested = details.error;
  if (!nested || typeof nested !== 'object') return false;
  return (
    'chargeId' in nested &&
    typeof nested.chargeId === 'string' &&
    nested.chargeId.length > 0 &&
    'allocationStatus' in nested &&
    nested.allocationStatus === 'blocked'
  );
}

function warehouseChargeError(error: unknown): string {
  const code = chargeCode(error);
  const messages: Record<string, string> = {
    IDEMPOTENCY_CONFLICT: 'This charge was already submitted with different details.',
    SOURCE_NOT_FOUND: 'The selected warehouse source is no longer available.',
    SOURCE_FACILITY_MISMATCH: 'The selected source belongs to another facility.',
    LOT_FACILITY_MISMATCH: 'The selected source belongs to another facility.',
    FACILITY_FORBIDDEN: 'This account cannot change charges for that facility.',
    FACILITY_REQUIRED: 'Select a warehouse facility before recording work.',
    FACILITY_NOT_FOUND: 'The selected warehouse facility is no longer available.',
    INVALID_QUANTITY: 'Enter a valid positive quantity.',
    QUANTITY_OUT_OF_RANGE: 'The quantity is outside the supported range.',
    CHARGE_AMOUNT_OUT_OF_RANGE: 'The charge amount is outside the supported range.',
    REVERSAL_NOT_ALLOWED: 'This charge cannot be reversed in its current state.',
    CHARGE_INVOICED: 'Invoiced charges cannot be reversed.',
    ALREADY_REVERSED: 'This charge has already been reversed.',
  };
  if (code && messages[code]) return messages[code];
  if (code) return humanizeWarehouseBlocker(code);
  return error instanceof Error ? error.message : 'Warehouse charge update failed. Try again.';
}

function chargeSourceLabel(
  charge: ChargeResponse['charge'],
  shipments: WarehouseShipment[],
  boxes: WarehouseShipmentBox[]
): string {
  if (charge.sourceType === 'prep_box') return 'Prepared box';
  if (charge.sourceType === 'prep_batch') return 'Prep item treatment';
  if (charge.sourceShipmentId) {
    const shipment = shipments.find((item) => item.id === charge.sourceShipmentId);
    return shipment
      ? `Shipment · ${shipmentLabel(shipment)}${shipment.destinationFc?.trim() ? ` · ${shipment.destinationFc.trim()}` : ''}`
      : 'Shipment';
  }
  if (charge.sourceShipmentLineId) {
    for (const shipment of shipments) {
      const line = shipment.lines.find((item) => item.id === charge.sourceShipmentLineId);
      if (line)
        return `Shipment item · ${shipmentLabel(shipment)}${shipment.destinationFc?.trim() ? ` · ${shipment.destinationFc.trim()}` : ''} · ${line.sellerSku}`;
    }
    return 'Shipment item';
  }
  if (charge.sourceBoxId) {
    const box = boxes.find((item) => item.id === charge.sourceBoxId);
    if (!box) return 'Packing box';
    const shipment = shipments.find((item) => item.id === box.shipmentId);
    return shipment
      ? `Packing box · ${shipmentLabel(shipment)}${shipment.destinationFc?.trim() ? ` · ${shipment.destinationFc.trim()}` : ''} · Box ${box.boxNumber}`
      : `Packing box · Box ${box.boxNumber}`;
  }
  return humanizeWarehouseSource(charge.sourceType);
}

function chargeStateLabel(
  charge: ChargeResponse['charge'],
  reversedIds: ReadonlySet<string>
): string {
  if (charge.reversalOfChargeId) return 'Reversal';
  if (reversedIds.has(charge.id)) return 'Reversed';
  if (charge.allocationStatus === 'blocked') return humanizeWarehouseBlocker(charge.blockReason);
  switch (charge.activeInvoice?.status) {
    case 'draft':
      return 'Reserved in draft';
    case 'submitted':
      return 'Seller review';
    case 'disputed':
      return 'Needs changes';
    case 'approved':
      return 'Billed';
    case 'partially_paid':
      return 'Partially paid';
    case 'paid':
      return 'Paid';
    case 'void':
      return 'Voided';
    default:
      return 'Ready';
  }
}

function chargeSortTime(row: ChargeResponse): number {
  const parsed = Date.parse(row.charge.createdAt);
  return Number.isNaN(parsed) ? 0 : parsed;
}

type InvoiceDetail = WarehouseInvoiceDetailResponse;
type InvoiceSummary = WarehouseInvoiceSummary;

type InvoiceMoneyResult = { ok: true; cents: number } | { ok: false; error: string };

const MAX_INVOICE_CENTS = 2_147_483_647n;

export function parseInvoiceMoney(raw: string): InvoiceMoneyResult {
  if (raw.length === 0) return { ok: false, error: 'Enter an amount.' };
  if (raw.trim() !== raw || !/^\d+(?:\.\d{1,2})?$/.test(raw)) {
    return { ok: false, error: 'Enter a non-negative amount with up to two decimals.' };
  }
  const [whole, fraction = ''] = raw.split('.');
  const cents = BigInt(whole ?? '0') * 100n + BigInt(fraction.padEnd(2, '0'));
  if (cents > MAX_INVOICE_CENTS) return { ok: false, error: 'Amount is too large.' };
  return { ok: true, cents: Number(cents) };
}

function invoiceDateInput(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : '';
}

function todayDateInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function invoiceDateIso(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const iso = `${value}T00:00:00.000Z`;
  const date = new Date(iso);
  return !Number.isNaN(date.getTime()) && date.toISOString() === iso ? iso : null;
}

function invoiceDateLabel(value: string | null | undefined): string {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Date unavailable'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: 'UTC' }).format(date);
}

/** R2 item 1 (D5) — true only once the replan state has loaded (never
 * flashes true-then-false while it's loading) for an approved invoice
 * whose approval postdates (or the state has never reconciled) the
 * tenant's last COGS reconcile. */
export function cogsRecomputePending(
  invoice: { status: string; approvedAt: string | null },
  replanState: { lastReconciledAt: string | null } | null | undefined
): boolean {
  if (invoice.status !== 'approved' || !replanState) return false;
  if (replanState.lastReconciledAt == null || invoice.approvedAt == null) return true;
  return new Date(invoice.approvedAt).getTime() > new Date(replanState.lastReconciledAt).getTime();
}

type InvoicePaymentStatus = 'unpaid' | 'partial' | 'paid' | 'not_applicable';

function paymentStatusLabel(status: InvoicePaymentStatus): string {
  switch (status) {
    case 'unpaid':
      return 'Unpaid';
    case 'partial':
      return 'Partially paid';
    case 'paid':
      return 'Paid';
    case 'not_applicable':
      return 'Not applicable';
  }
}

function invoiceErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiCallError)) return null;
  const details = error.details;
  if (details && typeof details === 'object' && 'error' in details) {
    const nested = details.error;
    if (
      nested &&
      typeof nested === 'object' &&
      'code' in nested &&
      typeof nested.code === 'string'
    ) {
      return nested.code;
    }
  }
  return error.message.match(/\b[A-Z][A-Z0-9_]+\b/)?.[0] ?? null;
}

function invoiceErrorMessage(error: unknown): string {
  const code = invoiceErrorCode(error);
  const messages: Record<string, string> = {
    IDEMPOTENCY_CONFLICT: 'This invoice action was already submitted with different details.',
    INVOICE_CURRENCY_MISMATCH: 'All selected charges must use the invoice currency.',
    INVOICE_DISCOUNT_OUT_OF_RANGE: 'The discount is outside the supported invoice range.',
    INVOICE_TOTAL_OUT_OF_RANGE: 'The invoice total is outside the supported range.',
    INVOICE_SNAPSHOT_MISMATCH:
      'One or more charge snapshots changed. Refresh and review the invoice.',
    CHARGE_ALREADY_RESERVED: 'One or more charges are reserved on another invoice.',
    CHARGE_ALREADY_INVOICED: 'One or more charges are already invoiced.',
    CHARGE_ALLOCATION_BLOCKED: 'One or more charges need allocation attention before invoicing.',
    DISCOUNT_EXCEEDS_SUBTOTAL: 'The discount cannot exceed the invoice subtotal.',
    INVOICE_TOTAL_INVALID: 'The invoice total is outside the supported range.',
    FACILITY_FORBIDDEN: 'This account cannot change invoices for that facility.',
    INVOICE_IMMUTABLE: 'This invoice can no longer be changed.',
    INVALID_INVOICE_TRANSITION: 'This invoice cannot move to that status.',
    INVOICE_FACILITY_MISMATCH: 'This invoice belongs to another facility.',
    INVOICE_NOT_FOUND: 'The invoice is no longer available.',
    CHARGE_NOT_FOUND: 'One or more charges are no longer available.',
    CHARGE_RESERVED: 'One or more charges are reserved on another invoice.',
    PAYMENT_EXCEEDS_BALANCE: 'The payment exceeds the outstanding balance.',
    PAYMENT_NOT_FOUND: 'The payment is no longer available.',
    PAYMENT_ALREADY_REVERSED: 'This payment has already been reversed.',
    STALE_INVOICE:
      'The invoice changed on the server. Review the refreshed latest version before retrying.',
  };
  if (code && messages[code]) return messages[code];
  return error instanceof Error ? error.message : 'Invoice update failed. Try again.';
}

function isStaleInvoiceError(error: unknown): boolean {
  return (
    error instanceof ApiCallError &&
    error.status === 409 &&
    (invoiceErrorCode(error) === 'STALE_INVOICE' || /stale|version/i.test(error.message))
  );
}

function invoiceSourceLabel(charge: ChargeResponse['charge']): string {
  if (charge.sourceType === 'prep_box') return 'Prepared box';
  if (charge.sourceType === 'prep_batch') return 'Prep item treatment';
  if (charge.sourceShipmentLineId) return 'Shipment item';
  if (charge.sourceBoxId) return 'Packing box';
  if (charge.sourceShipmentId) return 'Shipment';
  return humanizeWarehouseSource(charge.sourceType);
}

function invoiceChargeEligible(
  rows: ChargeResponse[],
  facilityId: string,
  currency: string,
  ownInvoiceId?: string
): ChargeResponse[] {
  const reversed = new Set(
    rows
      .map((row) => row.charge.reversalOfChargeId)
      .filter((chargeId): chargeId is string => Boolean(chargeId))
  );
  const base = rows.filter((row) => !row.charge.reversalOfChargeId && !reversed.has(row.charge.id));
  const unreserved = eligibleWarehouseCharges(base, facilityId, currency);
  if (!ownInvoiceId) return unreserved;
  const own = base.filter(
    ({ charge }) =>
      charge.warehouseId === facilityId &&
      charge.currency === currency &&
      charge.amountCents > 0 &&
      charge.allocationStatus === 'ready' &&
      charge.activeInvoice?.id === ownInvoiceId
  );
  return [...new Map([...unreserved, ...own].map((row) => [row.charge.id, row])).values()];
}

function invoiceIsDraft(detail: InvoiceDetail | null): boolean {
  return detail?.invoice.status === 'draft' && detail.invoice.documentType === 'standard';
}

type AppendixLine = WarehouseInvoiceAppendixPresentation['lines'][number];

function appendixCategoryLabel(
  category: WarehouseInvoiceAppendixPresentation['groups'][number]['category']
): string {
  return category === 'material' ? 'Materials' : 'Services';
}

function appendixPrepLabel(line: AppendixLine): string {
  return line.prepReference ? `Prep ${line.prepReference}` : 'No prep reference';
}

function appendixPoLabel(line: AppendixLine): string {
  if (line.poReferences.length > 0) return `PO ${line.poReferences.join(', ')}`;
  if (line.poReference) return `PO ${line.poReference}`;
  return 'No PO reference';
}

function appendixReceiptLabel(line: AppendixLine): string {
  if (line.receiptIds.length > 0) return `Receipt ${line.receiptIds.join(', ')}`;
  if (line.receiptId) return `Receipt ${line.receiptId}`;
  return 'No receipt reference';
}

function appendixWorkerLabel(line: AppendixLine): string {
  return line.workerName ?? 'Worker not recorded';
}

function InvoicesWorkspace({
  facilityId,
  facilityName,
  canManageInvoiceLifecycle,
  setAnnouncement,
}: {
  facilityId: string;
  facilityName: string;
  canManageInvoiceLifecycle: boolean;
  setAnnouncement: (message: string) => void;
}) {
  const invoicesQuery = useWarehouseInvoices({ warehouseId: facilityId }, true);
  const chargesQuery = useWarehouseWorkCharges({ warehouseId: facilityId }, true);
  // R2 item 1 (D5) — read-only: an approved invoice whose approval postdates
  // the tenant's last COGS reconcile hasn't been folded into landed COGS
  // yet (nightly batch, or "Recompute now" on the Finance page).
  const cogsReplanQuery = useCogsReplanState(true);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [lastDetail, setLastDetail] = useState<InvoiceDetail | null>(null);
  const [staleRefreshPending, setStaleRefreshPending] = useState(false);
  const detailQuery = useWarehouseInvoice(
    facilityId,
    selectedInvoiceId ?? undefined,
    Boolean(selectedInvoiceId)
  );
  const createInvoice = useCreateWarehouseInvoice();
  const updateInvoice = useUpdateWarehouseInvoiceDraft();
  const submitInvoice = useSubmitWarehouseInvoice();
  const transitionInvoice = useTransitionWarehouseInvoice();
  const recordPayment = useRecordWarehouseInvoicePayment();
  const reversePayment = useReverseWarehouseInvoicePayment();
  const [editing, setEditing] = useState(false);
  const [selectedChargeIds, setSelectedChargeIds] = useState<string[]>([]);
  const [currency, setCurrency] = useState('');
  const [servicePeriodStart, setServicePeriodStart] = useState('');
  const [servicePeriodEnd, setServicePeriodEnd] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [discount, setDiscount] = useState('0.00');
  const [tax, setTax] = useState('0.00');
  const [validation, setValidation] = useState<Record<string, string>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [disputeReasonError, setDisputeReasonError] = useState<string | null>(null);
  const [focusDetail, setFocusDetail] = useState(false);
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('0.00');
  const [paymentDate, setPaymentDate] = useState(todayDateInput());
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentAmountError, setPaymentAmountError] = useState<string | null>(null);
  const [paymentDateError, setPaymentDateError] = useState<string | null>(null);
  const [reversingPayment, setReversingPayment] = useState<WarehouseInvoicePayment | null>(null);
  const [reversePaymentReason, setReversePaymentReason] = useState('');
  const [reversePaymentReasonError, setReversePaymentReasonError] = useState<string | null>(null);
  const submitTriggerRef = useRef<HTMLButtonElement | null>(null);
  const approveTriggerRef = useRef<HTMLButtonElement | null>(null);
  const disputeTriggerRef = useRef<HTMLButtonElement | null>(null);
  const recordPaymentTriggerRef = useRef<HTMLButtonElement | null>(null);
  const reversePaymentTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const detailHeadingRef = useRef<HTMLHeadingElement | null>(null);

  const summaries = ((invoicesQuery.data ?? []) as InvoiceSummary[]).filter(
    ({ invoice }) => invoice.documentType === 'standard'
  );
  const detail = lastDetail ?? (detailQuery.data as InvoiceDetail | undefined) ?? null;
  const chargeRows = (chargesQuery.data ?? []) as ChargeResponse[];
  const reversedChargeIds = new Set(
    chargeRows
      .map(({ charge }) => charge.reversalOfChargeId)
      .filter((chargeId): chargeId is string => Boolean(chargeId))
  );
  const cleanChargeRows = chargeRows.filter(
    ({ charge }) => !charge.reversalOfChargeId && !reversedChargeIds.has(charge.id)
  );
  const ownInvoiceId =
    editing && detail?.invoice.status === 'draft' ? detail.invoice.id : undefined;
  const chargeCurrencies = [
    ...new Set(
      cleanChargeRows
        .map(({ charge }) => charge.currency)
        .filter(
          (item) => invoiceChargeEligible(chargeRows, facilityId, item, ownInvoiceId).length > 0
        )
    ),
  ].sort();
  const candidates = currency
    ? invoiceChargeEligible(chargeRows, facilityId, currency, ownInvoiceId)
    : [];
  const candidateIds = new Set(candidates.map(({ charge }) => charge.id));
  const eligibleSelectedChargeIds = selectedChargeIds.filter((id) => candidateIds.has(id));
  const selectedCount = eligibleSelectedChargeIds.length;
  const detailQueryHealthy = Boolean(
    selectedInvoiceId &&
    !detailQuery.isLoading &&
    !detailQuery.isFetching &&
    !detailQuery.isError &&
    detailQuery.data &&
    (detailQuery.data as InvoiceDetail).invoice.id === selectedInvoiceId
  );
  const editorQueriesHealthy =
    !staleRefreshPending &&
    !chargesQuery.isLoading &&
    !chargesQuery.isFetching &&
    !chargesQuery.isError &&
    (!selectedInvoiceId || detailQueryHealthy);
  const chargeById = new Map(chargeRows.map((row) => [row.charge.id, row]));
  const parsedDiscountForKey =
    discount.trim() === '' ? { ok: true as const, cents: 0 } : parseInvoiceMoney(discount);
  const parsedTaxForKey =
    tax.trim() === '' ? { ok: true as const, cents: 0 } : parseInvoiceMoney(tax);
  const normalizedDiscount = parsedDiscountForKey.ok ? parsedDiscountForKey.cents : null;
  const normalizedTax = parsedTaxForKey.ok ? parsedTaxForKey.cents : null;
  const draftFormKey = JSON.stringify({
    operation: 'create',
    facilityId,
    currency: currency || null,
    chargeIds: [...eligibleSelectedChargeIds].sort(),
    servicePeriodStart: invoiceDateIso(servicePeriodStart),
    servicePeriodEnd: invoiceDateIso(servicePeriodEnd),
    issueDate: invoiceDateIso(issueDate),
    dueDate: dueDate ? invoiceDateIso(dueDate) : null,
    discountCents: normalizedDiscount,
    taxCents: normalizedTax,
  });
  const updateFormKey = JSON.stringify({
    operation: 'update',
    invoiceId: detail?.invoice.id,
    expectedVersion: detail?.invoice.version,
    currency: currency || null,
    chargeIds: [...eligibleSelectedChargeIds].sort(),
    servicePeriodStart: invoiceDateIso(servicePeriodStart),
    servicePeriodEnd: invoiceDateIso(servicePeriodEnd),
    issueDate: invoiceDateIso(issueDate),
    dueDate: dueDate ? invoiceDateIso(dueDate) : null,
    discountCents: normalizedDiscount,
    taxCents: normalizedTax,
    version: detail?.invoice.version ?? null,
  });
  const createKey = useWarehouseIdempotencyKey(draftFormKey);
  const updateKey = useWarehouseIdempotencyKey(updateFormKey);
  const submitKey = useWarehouseIdempotencyKey({
    operation: 'submit',
    invoiceId: detail?.invoice.id,
    expectedVersion: detail?.invoice.version,
  });
  const approveKey = useWarehouseIdempotencyKey({
    operation: 'approve',
    invoiceId: detail?.invoice.id,
    expectedVersion: detail?.invoice.version,
  });
  const disputeKey = useWarehouseIdempotencyKey({
    operation: 'dispute',
    invoiceId: detail?.invoice.id,
    expectedVersion: detail?.invoice.version,
    reason: disputeReason.trim(),
  });
  const parsedPaymentAmountForKey = parseInvoiceMoney(paymentAmount);
  const recordPaymentKey = useWarehouseIdempotencyKey({
    operation: 'record_payment',
    invoiceId: detail?.invoice.id,
    expectedVersion: detail?.invoice.version,
    amountCents: parsedPaymentAmountForKey.ok ? parsedPaymentAmountForKey.cents : null,
    paidAt: invoiceDateIso(paymentDate),
    reference: paymentReference.trim() || null,
  });
  const reversePaymentKey = useWarehouseIdempotencyKey({
    operation: 'reverse_payment',
    invoiceId: detail?.invoice.id,
    paymentId: reversingPayment?.id ?? null,
    expectedVersion: detail?.invoice.version,
    reason: reversePaymentReason.trim(),
  });
  const mutationPending =
    createInvoice.isPending ||
    updateInvoice.isPending ||
    submitInvoice.isPending ||
    transitionInvoice.isPending;

  function hydrateDetail(next: InvoiceDetail) {
    setLastDetail(next);
    setCurrency(next.invoice.currency);
    setServicePeriodStart(invoiceDateInput(next.invoice.servicePeriodStart));
    setServicePeriodEnd(invoiceDateInput(next.invoice.servicePeriodEnd));
    setIssueDate(invoiceDateInput(next.invoice.issueDate));
    setDueDate(invoiceDateInput(next.invoice.dueDate));
    setDiscount((next.invoice.discountCents / 100).toFixed(2));
    setTax((next.invoice.taxCents / 100).toFixed(2));
    setSelectedChargeIds(next.lines.map(({ line }) => line.chargeId));
  }

  useEffect(() => {
    if (chargeCurrencies.length === 1 && !currency && !detail) setCurrency(chargeCurrencies[0]!);
  }, [chargeCurrencies.join(','), currency, detail]);

  useEffect(() => {
    if (!detailQuery.data || !selectedInvoiceId) return;
    const next = detailQuery.data as InvoiceDetail;
    if (next.invoice.id !== selectedInvoiceId) return;
    if (
      lastDetail &&
      lastDetail.invoice.id === selectedInvoiceId &&
      (next.invoice.version < lastDetail.invoice.version ||
        JSON.stringify(next) === JSON.stringify(lastDetail))
    )
      return;
    hydrateDetail(next);
    if (editing && !invoiceIsDraft(next)) setEditing(false);
  }, [detailQuery.data, selectedInvoiceId, lastDetail, editing]);

  useEffect(() => {
    if (!focusDetail || !detailHeadingRef.current) return;
    detailHeadingRef.current.focus();
    setFocusDetail(false);
  }, [focusDetail, detail]);

  function resetEditor() {
    setSelectedInvoiceId(null);
    setLastDetail(null);
    setEditing(true);
    setSelectedChargeIds([]);
    setCurrency(chargeCurrencies.length === 1 ? chargeCurrencies[0]! : '');
    setServicePeriodStart('');
    setServicePeriodEnd('');
    setIssueDate('');
    setDueDate('');
    setDiscount('0.00');
    setTax('0.00');
    setValidation({});
    setErrorMessage(null);
  }

  function selectInvoice(summary: InvoiceSummary) {
    if (summary.invoice.documentType !== 'standard') return;
    setSelectedInvoiceId(summary.invoice.id);
    setLastDetail(null);
    setEditing(false);
    setErrorMessage(null);
  }

  function changeCurrency(next: string) {
    setCurrency(next);
    setSelectedChargeIds((current) =>
      current.filter((id) => chargeById.get(id)?.charge.currency === next)
    );
  }

  function toggleCharge(chargeId: string) {
    setSelectedChargeIds((current) =>
      current.includes(chargeId) ? current.filter((id) => id !== chargeId) : [...current, chargeId]
    );
  }

  function validate(): {
    start: string;
    end: string;
    issue: string;
    due: string | null;
    discount: number;
    tax: number;
  } | null {
    const next: Record<string, string> = {};
    const start = invoiceDateIso(servicePeriodStart);
    const end = invoiceDateIso(servicePeriodEnd);
    const issue = invoiceDateIso(issueDate);
    const due = dueDate ? invoiceDateIso(dueDate) : null;
    if (!start) next.start = 'Enter a valid service period start date.';
    if (!end) next.end = 'Enter a valid service period end date.';
    if (!issue) next.issue = 'Enter a valid issue date.';
    if (start && end && start > end) next.end = 'Service period end must be on or after start.';
    if (dueDate && (!due || (issue && due < issue)))
      next.due = 'Due date must be on or after issue date.';
    const parsedDiscount =
      discount.trim() === '' ? { ok: true as const, cents: 0 } : parseInvoiceMoney(discount);
    const parsedTax = tax.trim() === '' ? { ok: true as const, cents: 0 } : parseInvoiceMoney(tax);
    if (!parsedDiscount.ok) next.discount = parsedDiscount.error;
    if (!parsedTax.ok) next.tax = parsedTax.error;
    if (!currency) next.currency = 'Choose an invoice currency.';
    if (selectedCount === 0) next.charges = 'Select at least one eligible charge.';
    setValidation(next);
    if (
      Object.keys(next).length > 0 ||
      !start ||
      !end ||
      !issue ||
      !parsedDiscount.ok ||
      !parsedTax.ok
    )
      return null;
    return {
      start,
      end,
      issue,
      due,
      discount: parsedDiscount.cents,
      tax: parsedTax.cents,
    };
  }

  function refreshAfterError() {
    void invoicesQuery.refetch();
    void chargesQuery.refetch();
    if (selectedInvoiceId) void detailQuery.refetch();
  }

  function handleInvoiceError(error: unknown) {
    if (isStaleInvoiceError(error)) {
      setStaleRefreshPending(true);
      setErrorMessage(
        'The invoice changed on the server. Review the latest version before retrying.'
      );
      setAnnouncement(
        'Latest invoice version refreshed. Review the latest version before retrying.'
      );
      const detailRefresh = selectedInvoiceId
        ? detailQuery
            .refetch()
            .then((result) => {
              if (!result.data) return;
              const latest = result.data as InvoiceDetail;
              hydrateDetail(latest);
              setEditing(invoiceIsDraft(latest));
            })
            .finally(() => setStaleRefreshPending(false))
        : Promise.resolve().finally(() => setStaleRefreshPending(false));
      void Promise.all([invoicesQuery.refetch(), chargesQuery.refetch(), detailRefresh]);
      return;
    }
    setErrorMessage(invoiceErrorMessage(error));
    refreshAfterError();
  }

  async function handleSave() {
    if (!editorQueriesHealthy) return;
    const values = validate();
    if (!values) return;
    const chargeIds = [...new Set(eligibleSelectedChargeIds)].sort();
    setErrorMessage(null);
    try {
      if (detail && invoiceIsDraft(detail) && selectedInvoiceId) {
        const result = await updateInvoice.mutateAsync({
          invoiceId: selectedInvoiceId,
          chargeIds,
          servicePeriodStart: values.start,
          servicePeriodEnd: values.end,
          issueDate: values.issue,
          dueDate: values.due,
          discountCents: values.discount,
          taxCents: values.tax,
          expectedVersion: detail.invoice.version,
          idempotencyKey: updateKey,
        });
        const next = result as InvoiceDetail;
        setLastDetail(next);
        setSelectedInvoiceId(next.invoice.id);
        setEditing(next.invoice.status === 'draft');
        setAnnouncement('Invoice draft saved.');
      } else {
        const result = await createInvoice.mutateAsync({
          warehouseId: facilityId,
          chargeIds,
          servicePeriodStart: values.start,
          servicePeriodEnd: values.end,
          issueDate: values.issue,
          dueDate: values.due,
          discountCents: values.discount,
          taxCents: values.tax,
          idempotencyKey: createKey,
        });
        const next = result as InvoiceDetail;
        setLastDetail(next);
        setSelectedInvoiceId(next.invoice.id);
        setEditing(next.invoice.status === 'draft');
        setAnnouncement(
          next.invoice.status === 'draft' ? 'Invoice draft created.' : 'Invoice created.'
        );
      }
    } catch (error: unknown) {
      handleInvoiceError(error);
    }
  }

  async function handleSubmit() {
    if (!detail || !invoiceIsDraft(detail) || !editorQueriesHealthy) return;
    setErrorMessage(null);
    try {
      const result = await submitInvoice.mutateAsync({
        invoiceId: detail.invoice.id,
        expectedVersion: detail.invoice.version,
        idempotencyKey: submitKey,
      });
      setLastDetail(result as InvoiceDetail);
      setEditing(false);
      setSubmitOpen(false);
      setFocusDetail(true);
      setAnnouncement('Invoice submitted for seller review.');
    } catch (error: unknown) {
      setSubmitOpen(false);
      queueMicrotask(() => submitTriggerRef.current?.focus());
      handleInvoiceError(error);
    }
  }

  function closeApproveDialog() {
    setApproveOpen(false);
    queueMicrotask(() => approveTriggerRef.current?.focus());
  }

  async function handleApprove() {
    if (!detail || detail.invoice.status !== 'submitted') return;
    setErrorMessage(null);
    try {
      const result = await transitionInvoice.mutateAsync({
        invoiceId: detail.invoice.id,
        warehouseId: detail.invoice.warehouseId,
        action: 'approve',
        expectedVersion: detail.invoice.version,
        idempotencyKey: approveKey,
      });
      setLastDetail(result as InvoiceDetail);
      setApproveOpen(false);
      setFocusDetail(true);
      setAnnouncement('Invoice approved.');
    } catch (error: unknown) {
      setApproveOpen(false);
      queueMicrotask(() => approveTriggerRef.current?.focus());
      handleInvoiceError(error);
    }
  }

  function closeDisputeDialog() {
    setDisputeOpen(false);
    setDisputeReason('');
    setDisputeReasonError(null);
    queueMicrotask(() => disputeTriggerRef.current?.focus());
  }

  async function handleDispute() {
    if (!detail || detail.invoice.status !== 'submitted') return;
    const reason = disputeReason.trim();
    if (!reason) {
      setDisputeReasonError('Enter a reason before disputing this invoice.');
      return;
    }
    setDisputeReasonError(null);
    setErrorMessage(null);
    try {
      const result = await transitionInvoice.mutateAsync({
        invoiceId: detail.invoice.id,
        warehouseId: detail.invoice.warehouseId,
        action: 'dispute',
        reason,
        expectedVersion: detail.invoice.version,
        idempotencyKey: disputeKey,
      });
      setLastDetail(result as InvoiceDetail);
      setDisputeOpen(false);
      setDisputeReason('');
      setFocusDetail(true);
      setAnnouncement('Invoice disputed.');
    } catch (error: unknown) {
      setDisputeOpen(false);
      queueMicrotask(() => disputeTriggerRef.current?.focus());
      handleInvoiceError(error);
    }
  }

  function openRecordPaymentDialog() {
    if (!detail) return;
    setPaymentAmount((detail.balanceCents / 100).toFixed(2));
    setPaymentDate(todayDateInput());
    setPaymentReference('');
    setPaymentAmountError(null);
    setPaymentDateError(null);
    setRecordPaymentOpen(true);
  }

  function closeRecordPaymentDialog() {
    setRecordPaymentOpen(false);
    queueMicrotask(() => recordPaymentTriggerRef.current?.focus());
  }

  async function handleRecordPayment() {
    if (!detail) return;
    const parsed = parseInvoiceMoney(paymentAmount);
    if (!parsed.ok) {
      setPaymentAmountError(parsed.error);
      return;
    }
    if (parsed.cents <= 0) {
      setPaymentAmountError('Enter a payment amount greater than zero.');
      return;
    }
    if (parsed.cents > detail.balanceCents) {
      setPaymentAmountError('Payment cannot exceed the outstanding balance.');
      return;
    }
    const paidAtIso = invoiceDateIso(paymentDate);
    if (!paidAtIso) {
      setPaymentDateError('Enter a valid payment date.');
      return;
    }
    setPaymentAmountError(null);
    setPaymentDateError(null);
    setErrorMessage(null);
    try {
      const result = await recordPayment.mutateAsync({
        invoiceId: detail.invoice.id,
        warehouseId: detail.invoice.warehouseId,
        amountCents: parsed.cents,
        paidAt: paidAtIso,
        reference: paymentReference.trim() ? paymentReference.trim() : null,
        expectedVersion: detail.invoice.version,
        idempotencyKey: recordPaymentKey,
      });
      setLastDetail({
        ...detail,
        invoice: result.invoice.invoice,
        paidCents: result.invoice.paidCents,
        balanceCents: result.invoice.balanceCents,
        paymentStatus: result.invoice.paymentStatus,
        payments: [...detail.payments, result.payment],
      });
      setRecordPaymentOpen(false);
      setFocusDetail(true);
      setAnnouncement('Payment recorded.');
    } catch (error: unknown) {
      setRecordPaymentOpen(false);
      queueMicrotask(() => recordPaymentTriggerRef.current?.focus());
      handleInvoiceError(error);
    }
  }

  function focusReversePaymentTrigger(paymentId: string | null) {
    if (!paymentId) return;
    queueMicrotask(() => reversePaymentTriggerRefs.current[paymentId]?.focus());
  }

  function closeReversePaymentDialog() {
    const paymentId = reversingPayment?.id ?? null;
    setReversingPayment(null);
    setReversePaymentReason('');
    setReversePaymentReasonError(null);
    focusReversePaymentTrigger(paymentId);
  }

  async function handleReversePayment() {
    if (!detail || !reversingPayment) return;
    const reason = reversePaymentReason.trim();
    if (!reason) {
      setReversePaymentReasonError('Enter a reason before reversing this payment.');
      return;
    }
    setReversePaymentReasonError(null);
    setErrorMessage(null);
    try {
      const result = await reversePayment.mutateAsync({
        invoiceId: detail.invoice.id,
        warehouseId: detail.invoice.warehouseId,
        paymentId: reversingPayment.id,
        reason,
        expectedVersion: detail.invoice.version,
        idempotencyKey: reversePaymentKey,
      });
      const paymentId = reversingPayment.id;
      setLastDetail({
        ...detail,
        invoice: result.invoice.invoice,
        paidCents: result.invoice.paidCents,
        balanceCents: result.invoice.balanceCents,
        paymentStatus: result.invoice.paymentStatus,
        payments: detail.payments.map((payment) =>
          payment.id === result.payment.id ? result.payment : payment
        ),
      });
      setReversingPayment(null);
      setReversePaymentReason('');
      setAnnouncement('Payment reversed.');
      focusReversePaymentTrigger(paymentId);
    } catch (error: unknown) {
      const paymentId = reversingPayment.id;
      setReversingPayment(null);
      focusReversePaymentTrigger(paymentId);
      handleInvoiceError(error);
    }
  }

  function renderValidation(id: string) {
    return validation[id] ? (
      <p id={`invoice-${id}-error`} className="text-sm text-negative mt-1">
        {validation[id]}
      </p>
    ) : null;
  }

  function renderEditor() {
    return (
      <Card>
        <h2 className="text-lg font-medium">
          {detail ? 'Edit invoice draft' : 'New invoice draft'}
        </h2>
        <p className="text-sm text-fg-secondary mt-1">
          Select finalized warehouse work charges for this facility.
        </p>
        <div className="grid gap-4 sm:grid-cols-2 mt-4">
          <div>
            <label htmlFor="invoice-service-period-start" className="block text-sm font-medium">
              Service period start
            </label>
            <input
              id="invoice-service-period-start"
              type="date"
              value={servicePeriodStart}
              onChange={(event) => setServicePeriodStart(event.target.value)}
              aria-invalid={Boolean(validation.start)}
              aria-describedby={validation.start ? 'invoice-start-error' : undefined}
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            />
            {renderValidation('start')}
          </div>
          <div>
            <label htmlFor="invoice-service-period-end" className="block text-sm font-medium">
              Service period end
            </label>
            <input
              id="invoice-service-period-end"
              type="date"
              value={servicePeriodEnd}
              onChange={(event) => setServicePeriodEnd(event.target.value)}
              aria-invalid={Boolean(validation.end)}
              aria-describedby={validation.end ? 'invoice-end-error' : undefined}
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            />
            {renderValidation('end')}
          </div>
          <div>
            <label htmlFor="invoice-issue-date" className="block text-sm font-medium">
              Issue date
            </label>
            <input
              id="invoice-issue-date"
              type="date"
              value={issueDate}
              onChange={(event) => setIssueDate(event.target.value)}
              aria-invalid={Boolean(validation.issue)}
              aria-describedby={validation.issue ? 'invoice-issue-error' : undefined}
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            />
            {renderValidation('issue')}
          </div>
          <div>
            <label htmlFor="invoice-due-date" className="block text-sm font-medium">
              Due date (optional)
            </label>
            <input
              id="invoice-due-date"
              type="date"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              aria-invalid={Boolean(validation.due)}
              aria-describedby={validation.due ? 'invoice-due-error' : undefined}
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            />
            {renderValidation('due')}
          </div>
          <div>
            <label htmlFor="invoice-currency" className="block text-sm font-medium">
              Currency
            </label>
            <select
              id="invoice-currency"
              value={currency}
              disabled={Boolean(detail)}
              onChange={(event) => changeCurrency(event.target.value)}
              aria-invalid={Boolean(validation.currency)}
              aria-describedby={validation.currency ? 'invoice-currency-error' : undefined}
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            >
              <option value="">Choose currency</option>
              {chargeCurrencies.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            {renderValidation('currency')}
          </div>
          <div>
            <label htmlFor="invoice-discount" className="block text-sm font-medium">
              Discount
            </label>
            <input
              id="invoice-discount"
              inputMode="decimal"
              value={discount}
              onChange={(event) => setDiscount(event.target.value)}
              aria-invalid={Boolean(validation.discount)}
              aria-describedby={validation.discount ? 'invoice-discount-error' : undefined}
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            />
            {renderValidation('discount')}
          </div>
          <div>
            <label htmlFor="invoice-tax" className="block text-sm font-medium">
              Tax
            </label>
            <input
              id="invoice-tax"
              inputMode="decimal"
              value={tax}
              onChange={(event) => setTax(event.target.value)}
              aria-invalid={Boolean(validation.tax)}
              aria-describedby={validation.tax ? 'invoice-tax-error' : undefined}
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            />
            {renderValidation('tax')}
          </div>
        </div>
        <div className="mt-5">
          <h3 className="text-sm font-medium">Eligible charges</h3>
          {chargesQuery.isLoading ? (
            <div className="mt-2 space-y-2" aria-hidden="true">
              {Array.from({ length: 3 }, (_, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Skeleton className="h-4 w-4 rounded-xs mt-0.5" />
                  <SkeletonLine className="w-2/3" />
                </div>
              ))}
            </div>
          ) : chargesQuery.isError ? (
            <p className="text-sm text-negative mt-2">
              Could not load charges.{' '}
              <button
                type="button"
                onClick={() => void chargesQuery.refetch()}
                className="underline"
              >
                Retry
              </button>
            </p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-fg-tertiary mt-2">No eligible charges for this currency.</p>
          ) : (
            <div className="mt-2 space-y-2">
              {candidates.map(({ charge }) => (
                <label key={charge.id} className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedChargeIds.includes(charge.id)}
                    onChange={() => toggleCharge(charge.id)}
                    aria-label={`${charge.code} ${charge.description}`}
                  />
                  <span>
                    <strong>{charge.code}</strong> · {charge.description} ·{' '}
                    {invoiceSourceLabel(charge)} ·{' '}
                    {formatWarehouseQuantityMillis(charge.quantityMillis)} ·{' '}
                    {formatWarehouseCents(charge.amountCents, charge.currency)}
                  </span>
                </label>
              ))}
            </div>
          )}
          {renderValidation('charges')}
        </div>
        <div className="flex flex-wrap gap-2 mt-5">
          <Button
            type="button"
            onClick={() => void handleSave()}
            disabled={mutationPending || !editorQueriesHealthy}
          >
            {mutationPending ? 'Saving…' : detail ? 'Save draft' : 'Create draft'}
          </Button>
          {detail && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditing(false)}
              disabled={mutationPending}
            >
              Cancel
            </Button>
          )}
        </div>
      </Card>
    );
  }

  function renderDetail() {
    if (!detail) return null;
    const invoice = detail.invoice;
    return (
      <Card>
        <h2 ref={detailHeadingRef} tabIndex={-1} className="text-lg font-medium">
          Invoice details
        </h2>
        <div className="grid gap-3 sm:grid-cols-3 mt-4 text-sm">
          <p>
            <span className="text-fg-tertiary">Facility</span>
            <br />
            {facilityName}
          </p>
          <p>
            <span className="text-fg-tertiary">Service period</span>
            <br />
            {invoiceDateLabel(invoice.servicePeriodStart)} –{' '}
            {invoiceDateLabel(invoice.servicePeriodEnd)}
          </p>
          <p>
            <span className="text-fg-tertiary">Issue / due</span>
            <br />
            {invoiceDateLabel(invoice.issueDate)} / {invoiceDateLabel(invoice.dueDate)}
          </p>
          <p>
            <span className="text-fg-tertiary">Status</span>
            <br />
            {invoiceStatusLabel(invoice.status)}
            {cogsRecomputePending(invoice, cogsReplanQuery.data?.state) && (
              <>
                {' '}
                <Badge variant="warning">COGS recompute pending</Badge>
              </>
            )}
          </p>
          <p>
            <span className="text-fg-tertiary">Subtotal</span>
            <br />
            {formatWarehouseCents(invoice.subtotalCents, invoice.currency)}
          </p>
          <p>
            <span className="text-fg-tertiary">Discount</span>
            <br />
            {formatWarehouseCents(invoice.discountCents, invoice.currency)}
          </p>
          <p>
            <span className="text-fg-tertiary">Tax</span>
            <br />
            {formatWarehouseCents(invoice.taxCents, invoice.currency)}
          </p>
          <p>
            <span className="text-fg-tertiary">Total</span>
            <br />
            {formatWarehouseCents(invoice.totalCents, invoice.currency)}
          </p>
          <p>
            <span className="text-fg-tertiary">Paid</span>
            <br />
            {formatWarehouseCents(detail.paidCents, invoice.currency)}
            {canManageInvoiceLifecycle &&
              ['approved', 'partially_paid'].includes(invoice.status) && (
                <>
                  {' '}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    ref={recordPaymentTriggerRef}
                    onClick={openRecordPaymentDialog}
                    disabled={recordPayment.isPending}
                  >
                    Record payment
                  </Button>
                </>
              )}
          </p>
          <p>
            <span className="text-fg-tertiary">Balance</span>
            <br />
            {formatWarehouseCents(detail.balanceCents, invoice.currency)} ·{' '}
            {paymentStatusLabel(detail.paymentStatus)}
          </p>
        </div>
        <h3 className="text-sm font-medium mt-5">Frozen charge lines</h3>
        <div className="mt-2 space-y-2">
          {detail.lines.map(({ line }) => (
            <div key={line.id} className="border border-border-subtle rounded-sm p-3 text-sm">
              <div className="flex justify-between gap-3">
                <span>
                  <strong>{line.code}</strong> · {line.description}
                </span>
                <span>{formatWarehouseCents(line.netAmountCents, line.currency)}</span>
              </div>
              <p className="text-xs text-fg-tertiary mt-1">
                {formatWarehouseQuantityMillis(line.quantityMillis)} ×{' '}
                {formatWarehouseCents(line.rateCents, line.currency)} · Gross{' '}
                {formatWarehouseCents(line.amountCents, line.currency)} · Discount{' '}
                {formatWarehouseCents(line.discountCents, line.currency)} · Net{' '}
                {formatWarehouseCents(line.netAmountCents, line.currency)} · {line.currency}
              </p>
              <p className="text-xs text-fg-tertiary mt-1">
                {humanizeWarehouseBasis(line.billingBasis)} ·{' '}
                {accountingTreatmentLabel(line.accountingTreatment)}
              </p>
              {line.reservationReleasedAt && (
                <p className="text-xs text-fg-tertiary mt-1">
                  Reservation released {invoiceDateLabel(line.reservationReleasedAt)}
                </p>
              )}
            </div>
          ))}
        </div>
        {detail.payments.length > 0 && (
          <>
            <h3 className="text-sm font-medium mt-5">Payment history</h3>
            <ul className="mt-2 space-y-1 text-sm">
              {detail.payments.map((payment) => (
                <li key={payment.id} className="flex flex-wrap items-center gap-2">
                  <span>
                    {invoiceDateLabel(payment.paidAt)} ·{' '}
                    {formatWarehouseCents(payment.amountCents, invoice.currency)}
                    {payment.reference ? ` · ${payment.reference}` : ''} ·{' '}
                    {payment.reversedAt ? 'No longer applied' : 'Applied'}
                  </span>
                  {canManageInvoiceLifecycle && !payment.reversedAt && (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={reversePayment.isPending}
                      ref={(element) => {
                        reversePaymentTriggerRefs.current[payment.id] = element;
                      }}
                      onClick={() => {
                        setReversePaymentReasonError(null);
                        setReversePaymentReason('');
                        setReversingPayment(payment);
                      }}
                    >
                      Reverse
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        {detail.appendix && (
          <>
            <h3 className="text-sm font-medium mt-5">Invoice appendix</h3>
            <p className="text-xs text-fg-tertiary mt-1">
              Frozen snapshot · {invoiceDateLabel(detail.appendix.frozenAt)} · Version{' '}
              {detail.appendix.version}
            </p>
            {detail.appendix.fingerprint && (
              <p
                className="text-[11px] text-fg-tertiary/70 mt-0.5"
                title={detail.appendix.fingerprint}
              >
                Evidence fingerprint recorded
              </p>
            )}
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {detail.appendix.groups.map((group) => (
                <div
                  key={group.category}
                  className="border border-border-subtle rounded-sm p-3 text-sm"
                >
                  <p className="font-medium">{appendixCategoryLabel(group.category)}</p>
                  <p className="text-xs text-fg-tertiary mt-1">
                    {group.lineCount} {group.lineCount === 1 ? 'line' : 'lines'} · Gross{' '}
                    {formatWarehouseCents(group.grossCents, invoice.currency)} · Discount{' '}
                    {formatWarehouseCents(group.discountCents, invoice.currency)} · Net{' '}
                    {formatWarehouseCents(group.netCents, invoice.currency)}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-2">
              {detail.appendix.lines.map((line) => (
                <div key={line.id} className="border border-border-subtle rounded-sm p-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <span>
                      <strong>{line.code}</strong> · {line.description}
                    </span>
                    <span>{formatWarehouseCents(line.netCents, invoice.currency)}</span>
                  </div>
                  <p className="text-xs text-fg-tertiary mt-1">
                    {appendixPrepLabel(line)} · {appendixPoLabel(line)} ·{' '}
                    {appendixReceiptLabel(line)} · {appendixWorkerLabel(line)}
                  </p>
                  {line.allocations.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-xs text-fg-tertiary">
                      {line.allocations.map((allocation, index) => (
                        <li key={`${line.id}-allocation-${index}`}>
                          {allocation.prepBoxReference ?? 'Box unavailable'}
                          {allocation.sellerSku ? ` · ${allocation.sellerSku}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        {invoice.status === 'draft' && (
          <div className="flex flex-wrap gap-2 mt-5">
            <Button type="button" variant="secondary" onClick={() => setEditing(true)}>
              Edit draft
            </Button>
            <Button
              type="button"
              onClick={() => setSubmitOpen(true)}
              ref={submitTriggerRef}
              disabled={mutationPending || !editorQueriesHealthy}
            >
              Submit for seller review
            </Button>
          </div>
        )}
        {invoice.status === 'submitted' && (
          <div
            className="flex flex-wrap items-center gap-2 mt-5"
            data-invoice-transition-actions="true"
          >
            {canManageInvoiceLifecycle ? (
              <>
                <Button
                  type="button"
                  onClick={() => setApproveOpen(true)}
                  ref={approveTriggerRef}
                  disabled={mutationPending}
                >
                  Approve
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setDisputeOpen(true)}
                  ref={disputeTriggerRef}
                  disabled={mutationPending}
                >
                  Dispute
                </Button>
              </>
            ) : (
              <p className="text-sm text-fg-tertiary">Awaiting owner approval</p>
            )}
          </div>
        )}
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-invoice-layout="responsive">
      <section aria-labelledby="warehouse-invoices-heading">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="warehouse-invoices-heading" className="text-lg font-medium">
              Invoices
            </h2>
            <p className="text-sm text-fg-secondary mt-1">
              Create and submit standard warehouse service invoices for {facilityName}.
            </p>
          </div>
          <Button id="warehouse-invoice-new" type="button" onClick={resetEditor}>
            New draft
          </Button>
        </div>
        {invoicesQuery.isLoading && (
          <div className="mt-3 space-y-2" aria-hidden="true">
            {Array.from({ length: 3 }, (_, i) => (
              <div
                key={i}
                className="border border-border-subtle rounded-sm p-3 grid gap-2 sm:grid-cols-6"
              >
                <SkeletonLine className="w-20" />
                <SkeletonLine className="w-28" />
                <SkeletonLine className="w-16" />
                <SkeletonLine className="w-16" />
                <SkeletonLine className="w-16" />
                <SkeletonLine className="w-16" />
              </div>
            ))}
          </div>
        )}
        {invoicesQuery.isError && (
          <p className="text-sm text-negative mt-3">
            Could not load invoice history.{' '}
            <button
              type="button"
              onClick={() => void invoicesQuery.refetch()}
              className="underline"
            >
              Retry
            </button>
          </p>
        )}
        {!invoicesQuery.isLoading && !invoicesQuery.isError && summaries.length === 0 && (
          <p className="text-sm text-fg-tertiary mt-3">No standard warehouse invoices yet.</p>
        )}
        {summaries.length > 0 && (
          <div
            className="mt-3 space-y-2"
            data-invoice-columns="Invoice|Service period|Total|Status|Payment|Action"
          >
            <div className="hidden sm:grid sm:grid-cols-6 gap-2 px-3 text-xs uppercase tracking-wide text-fg-tertiary">
              <span>Invoice</span>
              <span>Service period</span>
              <span>Total</span>
              <span>Status</span>
              <span>Payment</span>
              <span>Action</span>
            </div>
            {summaries.map((summary) => (
              <button
                key={summary.invoice.id}
                type="button"
                onClick={() => selectInvoice(summary)}
                className="w-full text-left border border-border-subtle rounded-sm p-3 grid gap-2 sm:grid-cols-6 text-sm"
              >
                <span>
                  <span className="text-fg-tertiary sm:hidden">Invoice · </span>
                  {invoiceDateLabel(summary.invoice.issueDate)}
                </span>
                <span>
                  <span className="text-fg-tertiary sm:hidden">Service period · </span>
                  {invoiceDateLabel(summary.invoice.servicePeriodStart)} –{' '}
                  {invoiceDateLabel(summary.invoice.servicePeriodEnd)}
                </span>
                <span>
                  <span className="text-fg-tertiary sm:hidden">Total · </span>
                  {formatWarehouseCents(summary.invoice.totalCents, summary.invoice.currency)}
                </span>
                <span>
                  <span className="text-fg-tertiary sm:hidden">Status · </span>
                  {invoiceStatusLabel(summary.invoice.status)}
                </span>
                <span>
                  <span className="text-fg-tertiary sm:hidden">Payment · </span>
                  {paymentStatusLabel(summary.paymentStatus)}
                </span>
                <span className="text-accent-fg">View details</span>
              </button>
            ))}
          </div>
        )}
      </section>
      {detailQuery.isLoading && selectedInvoiceId && (
        <Card aria-hidden="true">
          <SkeletonLine className="h-5 w-40" />
          <div className="grid gap-3 sm:grid-cols-3 mt-4">
            {Array.from({ length: 9 }, (_, i) => (
              <div key={i}>
                <SkeletonLine className="w-20 h-3" />
                <SkeletonLine className="w-24 mt-2" />
              </div>
            ))}
          </div>
          <SkeletonLine className="h-4 w-32 mt-5" />
          <div className="mt-2 space-y-2">
            {Array.from({ length: 2 }, (_, i) => (
              <div key={i} className="border border-border-subtle rounded-sm p-3">
                <SkeletonLine className="w-2/3" />
                <SkeletonLine className="w-1/2 mt-2 h-3" />
              </div>
            ))}
          </div>
        </Card>
      )}
      {detailQuery.isError && selectedInvoiceId && (
        <p className="text-sm text-negative">
          Could not load invoice details.{' '}
          <button type="button" onClick={() => void detailQuery.refetch()} className="underline">
            Retry
          </button>
        </p>
      )}
      {editing && (detail === null || invoiceIsDraft(detail)) && renderEditor()}
      {!editing && renderDetail()}
      <div id="invoice-error" role="alert" className="text-sm text-negative min-h-5">
        {errorMessage}
      </div>
      <Dialog
        open={submitOpen}
        onOpenChange={(open) => {
          if (!open) {
            setSubmitOpen(false);
            queueMicrotask(() => submitTriggerRef.current?.focus());
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit invoice?</DialogTitle>
            <DialogDescription>
              Submit the {facilityName} invoice for{' '}
              {invoiceDateLabel(detail?.invoice.servicePeriodStart)} –{' '}
              {invoiceDateLabel(detail?.invoice.servicePeriodEnd)} totaling{' '}
              {detail
                ? formatWarehouseCents(detail.invoice.totalCents, detail.invoice.currency)
                : ''}
              . Seller review will begin after submission.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setSubmitOpen(false);
                queueMicrotask(() => submitTriggerRef.current?.focus());
              }}
              disabled={submitInvoice.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleSubmit()}
              disabled={submitInvoice.isPending || !editorQueriesHealthy}
            >
              {submitInvoice.isPending ? 'Submitting…' : 'Submit for seller review'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={approveOpen}
        onOpenChange={(open) => {
          if (!open) closeApproveDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve invoice?</DialogTitle>
            <DialogDescription>
              Approving posts this invoice&apos;s costs to inventory and expenses and recomputes
              COGS. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={closeApproveDialog}
              disabled={transitionInvoice.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleApprove()}
              disabled={transitionInvoice.isPending}
            >
              {transitionInvoice.isPending ? 'Approving…' : 'Approve invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={disputeOpen}
        onOpenChange={(open) => {
          if (!open) closeDisputeDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dispute invoice?</DialogTitle>
            <DialogDescription>
              Disputing flags this invoice for review. Explain what needs to change before it can be
              approved.
            </DialogDescription>
          </DialogHeader>
          <label
            htmlFor="warehouse-invoice-dispute-reason"
            className="block text-sm font-medium mt-3"
          >
            Reason
          </label>
          <textarea
            id="warehouse-invoice-dispute-reason"
            value={disputeReason}
            onChange={(event) => {
              setDisputeReason(event.target.value);
              setDisputeReasonError(null);
            }}
            aria-invalid={Boolean(disputeReasonError)}
            aria-describedby={
              disputeReasonError ? 'warehouse-invoice-dispute-reason-error' : undefined
            }
            rows={3}
            className="w-full px-3 py-2 mt-2 bg-surface border border-border-default rounded-sm text-sm"
          />
          {disputeReasonError && (
            <p id="warehouse-invoice-dispute-reason-error" className="text-sm text-negative mt-1">
              {disputeReasonError}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={closeDisputeDialog}
              disabled={transitionInvoice.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleDispute()}
              disabled={transitionInvoice.isPending}
            >
              {transitionInvoice.isPending ? 'Disputing…' : 'Submit dispute'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={recordPaymentOpen}
        onOpenChange={(open) => {
          if (!open) closeRecordPaymentDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record payment</DialogTitle>
            <DialogDescription>
              Record a payment against this invoice. The outstanding balance is prefilled below.
            </DialogDescription>
          </DialogHeader>
          <label htmlFor="warehouse-payment-amount" className="block text-sm font-medium mt-3">
            Amount
          </label>
          <input
            id="warehouse-payment-amount"
            inputMode="decimal"
            value={paymentAmount}
            onChange={(event) => {
              setPaymentAmount(event.target.value);
              setPaymentAmountError(null);
            }}
            aria-invalid={Boolean(paymentAmountError)}
            aria-describedby={paymentAmountError ? 'warehouse-payment-amount-error' : undefined}
            className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
          />
          {paymentAmountError && (
            <p id="warehouse-payment-amount-error" className="text-sm text-negative mt-1">
              {paymentAmountError}
            </p>
          )}
          <label htmlFor="warehouse-payment-date" className="block text-sm font-medium mt-3">
            Paid date
          </label>
          <input
            id="warehouse-payment-date"
            type="date"
            value={paymentDate}
            onChange={(event) => {
              setPaymentDate(event.target.value);
              setPaymentDateError(null);
            }}
            aria-invalid={Boolean(paymentDateError)}
            aria-describedby={paymentDateError ? 'warehouse-payment-date-error' : undefined}
            className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
          />
          {paymentDateError && (
            <p id="warehouse-payment-date-error" className="text-sm text-negative mt-1">
              {paymentDateError}
            </p>
          )}
          <label htmlFor="warehouse-payment-reference" className="block text-sm font-medium mt-3">
            Reference (optional)
          </label>
          <input
            id="warehouse-payment-reference"
            value={paymentReference}
            onChange={(event) => setPaymentReference(event.target.value)}
            placeholder="e.g. Zelle 9/2"
            className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={closeRecordPaymentDialog}
              disabled={recordPayment.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleRecordPayment()}
              disabled={recordPayment.isPending}
            >
              {recordPayment.isPending ? 'Recording…' : 'Confirm payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={Boolean(reversingPayment)}
        onOpenChange={(open) => {
          if (!open) closeReversePaymentDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reverse payment</DialogTitle>
            <DialogDescription>
              This reverses{' '}
              {reversingPayment
                ? formatWarehouseCents(reversingPayment.amountCents, detail?.invoice.currency)
                : ''}{' '}
              and updates the invoice balance.
            </DialogDescription>
          </DialogHeader>
          <label
            htmlFor="warehouse-reverse-payment-reason"
            className="block text-sm font-medium mt-3"
          >
            Reason
          </label>
          <input
            id="warehouse-reverse-payment-reason"
            value={reversePaymentReason}
            onChange={(event) => {
              setReversePaymentReason(event.target.value);
              setReversePaymentReasonError(null);
            }}
            aria-invalid={Boolean(reversePaymentReasonError)}
            aria-describedby={
              reversePaymentReasonError ? 'warehouse-reverse-payment-reason-error' : undefined
            }
            className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
          />
          {reversePaymentReasonError && (
            <p id="warehouse-reverse-payment-reason-error" className="text-sm text-negative mt-1">
              {reversePaymentReasonError}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={closeReversePaymentDialog}
              disabled={reversePayment.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleReversePayment()}
              disabled={reversePayment.isPending}
            >
              {reversePayment.isPending ? 'Reversing…' : 'Reverse payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PackingWorkspace({
  facilityId,
  facilityName,
  warehouseRole,
  setAnnouncement,
}: {
  facilityId: string;
  facilityName: string;
  warehouseRole: boolean;
  setAnnouncement: (message: string) => void;
}) {
  const shipmentsQuery = useWarehouseBillingShipments(warehouseRole ? facilityId : undefined, true);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null);
  const [openBoxId, setOpenBoxId] = useState<string | null>(null);
  const [newBoxNumber, setNewBoxNumber] = useState('');
  const [invalidNewBoxNumber, setInvalidNewBoxNumber] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, Record<string, string>>>({});
  const [savedBoxes, setSavedBoxes] = useState<Record<string, PackingBox>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [invalidLineKey, setInvalidLineKey] = useState<string | null>(null);
  const [finalizeBoxId, setFinalizeBoxId] = useState<string | null>(null);
  const [focusFinalizedBoxId, setFocusFinalizedBoxId] = useState<string | null>(null);
  const finalizeTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const finalizedHeadingRefs = useRef<Record<string, HTMLHeadingElement | null>>({});

  const assignment = useAssignShipmentFacility();
  const createBox = useCreateShipmentBox();
  const replaceContents = useReplaceShipmentBoxContents();
  const finalizeBox = useFinalizeShipmentBox();

  const returnedShipments = shipmentsQuery.data ?? [];
  const assignedShipments = returnedShipments.filter(
    (shipment) => shipment.warehouseId === facilityId
  );
  const unassignedShipments = warehouseRole
    ? []
    : returnedShipments.filter((shipment) => shipment.warehouseId === null);
  const selectedShipment =
    assignedShipments.find((shipment) => shipment.id === selectedShipmentId) ?? null;
  const boxesQuery = useWarehouseShipmentBoxes(
    facilityId,
    selectedShipment?.id,
    Boolean(selectedShipment)
  );
  const serverBoxes = boxesQuery.data ?? [];
  const returnedOnlyBoxes = Object.values(savedBoxes).filter(
    (box) =>
      box.shipmentId === selectedShipment?.id &&
      !serverBoxes.some((serverBox) => serverBox.id === box.id)
  );
  const boxes = [...serverBoxes, ...returnedOnlyBoxes].map((box) => savedBoxes[box.id] ?? box);
  const candidateBox = boxes.find((box) => box.id === finalizeBoxId) ?? null;

  useEffect(() => {
    setSavedBoxes({});
    setDrafts({});
    setOpenBoxId(null);
  }, [selectedShipmentId]);

  useEffect(() => {
    if (!boxesQuery.data) return;
    setSavedBoxes((current) => {
      const next = { ...current };
      let changed = false;
      for (const [boxId, box] of Object.entries(next)) {
        if (box.shipmentId !== selectedShipment?.id) continue;
        delete next[boxId];
        changed = true;
      }
      return changed ? next : current;
    });
  }, [boxesQuery.data, selectedShipment?.id]);

  async function refetchBoxesAndClearOverlays() {
    try {
      const result = await boxesQuery.refetch();
      if (result.data) setSavedBoxes({});
    } catch {
      // Keep the local snapshot visible until a later authoritative refresh succeeds.
    }
  }

  useEffect(() => {
    if (!focusFinalizedBoxId) return;
    const heading = finalizedHeadingRefs.current[focusFinalizedBoxId];
    if (!heading) return;
    heading.focus();
    setFocusFinalizedBoxId(null);
  }, [boxes, focusFinalizedBoxId]);

  function handleAssignment(shipment: WarehouseShipment) {
    setErrorMessage(null);
    void assignment
      .mutateAsync({ shipmentId: shipment.id, warehouseId: facilityId })
      .then(() => {
        setAnnouncement(`Shipment ${shipment.shipmentId} assigned to this facility.`);
      })
      .catch((error: unknown) => setErrorMessage(packingErrorMessage(error)));
  }

  function handleCreateBox() {
    const boxNumber = newBoxNumber.trim();
    if (!boxNumber || !selectedShipment) {
      if (!boxNumber) setInvalidNewBoxNumber(true);
      setErrorMessage('Enter a box number before creating a box.');
      return;
    }
    setInvalidNewBoxNumber(false);
    setErrorMessage(null);
    void createBox
      .mutateAsync({ shipmentId: selectedShipment.id, boxNumber })
      .then((box) => {
        setSavedBoxes((current) => ({ ...current, [box.id]: box }));
        setNewBoxNumber('');
        setOpenBoxId(box.id);
        setAnnouncement(`Box ${box.boxNumber} is ready.`);
      })
      .catch((error: unknown) => {
        setErrorMessage(packingErrorMessage(error));
        void refetchBoxesAndClearOverlays();
      });
  }

  function draftValue(box: PackingBox, line: PackingLine): string {
    const value = drafts[box.id]?.[line.id];
    if (value !== undefined) return value;
    const existing = box.contents.find((content) => content.shipmentLineId === line.id);
    return existing ? String(existing.listingUnits) : '';
  }

  function hasUnsavedDrafts(box: PackingBox): boolean {
    const boxDrafts = drafts[box.id];
    if (!boxDrafts) return false;
    return Object.entries(boxDrafts).some(([lineId, raw]) => {
      const persistedUnits =
        box.contents.find((content) => content.shipmentLineId === lineId)?.listingUnits ?? 0;
      const trimmed = raw.trim();
      if (!trimmed || trimmed === '0') return persistedUnits !== 0;
      const parsed = safePositiveInteger(raw);
      return parsed === null || parsed !== persistedUnits;
    });
  }

  function setDraftValue(boxId: string, lineId: string, value: string) {
    setErrorMessage(null);
    setInvalidLineKey(null);
    setDrafts((current) => ({ ...current, [boxId]: { ...current[boxId], [lineId]: value } }));
  }

  function replacementContents(
    box: PackingBox
  ): { shipmentLineId: string; listingUnits: number }[] | null {
    if (!selectedShipment) return null;
    const contents: { shipmentLineId: string; listingUnits: number }[] = [];
    for (const line of selectedShipment.lines) {
      const raw = draftValue(box, line);
      const trimmed = raw.trim();
      if (!trimmed || trimmed === '0') continue;
      const parsed = safePositiveInteger(trimmed);
      const maximum = maxReplacementUnits(line, boxes, box, line.id);
      if (parsed === null || parsed > maximum) {
        setInvalidLineKey(`${box.id}:${line.id}`);
        setErrorMessage(
          `${line.sellerSku}: enter a whole number from 1 to ${maximum}, or leave it blank.`
        );
        return null;
      }
      contents.push({ shipmentLineId: line.id, listingUnits: parsed });
    }
    return contents;
  }

  function handleSaveBox(box: PackingBox) {
    const contents = replacementContents(box);
    if (!contents || !selectedShipment) return;
    setErrorMessage(null);
    setInvalidLineKey(null);
    void replaceContents
      .mutateAsync({ shipmentId: selectedShipment.id, boxId: box.id, contents })
      .then(async (savedBox) => {
        setSavedBoxes((current) => ({ ...current, [box.id]: savedBox }));
        setDrafts((current) => {
          const next = { ...current };
          delete next[box.id];
          return next;
        });
        setAnnouncement(`Box ${box.boxNumber} contents saved.`);
        await refetchBoxesAndClearOverlays();
      })
      .catch((error: unknown) => {
        setErrorMessage(packingErrorMessage(error));
        void refetchBoxesAndClearOverlays();
      });
  }

  function requestFinalize(box: PackingBox) {
    setErrorMessage(null);
    setInvalidLineKey(null);
    if (hasUnsavedDrafts(box)) {
      setErrorMessage('Save contents first before finalizing this box.');
      return;
    }
    if (box.contents.length === 0) {
      setErrorMessage('Add at least one item before finalizing this box.');
      return;
    }
    if (
      box.contents.some(
        (content) => content.listingUnits > 0 && content.unitsPerListingSnapshot === null
      )
    ) {
      setErrorMessage('This box cannot be finalized because a pack-size mapping is missing.');
      return;
    }
    setFinalizeBoxId(box.id);
  }

  function focusFinalizeTrigger(boxId: string | null) {
    if (!boxId) return;
    queueMicrotask(() => finalizeTriggerRefs.current[boxId]?.focus());
  }

  function closeFinalizeDialog() {
    const boxId = finalizeBoxId;
    setFinalizeBoxId(null);
    focusFinalizeTrigger(boxId);
  }

  function handleFinalize() {
    if (!candidateBox || !selectedShipment) return;
    void finalizeBox
      .mutateAsync({ shipmentId: selectedShipment.id, boxId: candidateBox.id })
      .then(async (finalizedBox) => {
        const boxId = candidateBox.id;
        setSavedBoxes((current) => ({ ...current, [boxId]: finalizedBox }));
        setFinalizeBoxId(null);
        setOpenBoxId(null);
        setFocusFinalizedBoxId(boxId);
        setAnnouncement(`Box ${candidateBox.boxNumber} finalized. Its contents are now read-only.`);
        await refetchBoxesAndClearOverlays();
      })
      .catch((error: unknown) => {
        setErrorMessage(packingErrorMessage(error));
        void refetchBoxesAndClearOverlays();
      });
  }

  if (shipmentsQuery.isLoading) {
    return (
      <div
        data-packing-layout="two-pane"
        className="grid gap-6 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]"
        aria-hidden="true"
      >
        <section className="space-y-3">
          <h2 className="text-lg font-medium">Shipments</h2>
          {Array.from({ length: 3 }, (_, i) => (
            <Card key={i}>
              <SkeletonLine className="w-2/3" />
              <SkeletonLine className="w-1/3 mt-2 h-3" />
              <div className="mt-3 grid grid-cols-2 gap-2">
                <SkeletonLine className="w-full h-3" />
                <SkeletonLine className="w-full h-3" />
              </div>
            </Card>
          ))}
        </section>
        <section className="space-y-3">
          <h2 className="text-lg font-medium">Boxes</h2>
          <Card>
            <SkeletonLine className="w-1/2" />
          </Card>
        </section>
      </div>
    );
  }
  if (shipmentsQuery.isError) {
    return (
      <Card>
        <p className="text-sm text-fg-secondary">Could not load shipments to pack.</p>
        <button
          type="button"
          onClick={() => void shipmentsQuery.refetch()}
          className="mt-3 text-sm text-accent-fg hover:underline"
        >
          Retry
        </button>
      </Card>
    );
  }
  if (assignedShipments.length === 0 && unassignedShipments.length === 0) {
    return (
      <Card>
        <p className="text-sm text-fg-secondary">No shipments are assigned to this facility yet.</p>
      </Card>
    );
  }

  return (
    <div
      data-packing-layout="two-pane"
      className="grid gap-6 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]"
    >
      <section aria-labelledby="packing-shipments-heading" className="space-y-3">
        <div>
          <h2 id="packing-shipments-heading" className="text-lg font-medium">
            Shipments
          </h2>
          <p className="text-sm text-fg-secondary">Choose the shipment you are packing.</p>
        </div>
        {assignedShipments.map((shipment) => (
          <Card
            key={shipment.id}
            className={selectedShipment?.id === shipment.id ? 'border-accent-primary' : undefined}
          >
            <button
              type="button"
              onClick={() => {
                setSelectedShipmentId(shipment.id);
                setErrorMessage(null);
              }}
              className="w-full text-left"
              aria-pressed={selectedShipment?.id === shipment.id}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium truncate">{shipmentLabel(shipment)}</p>
                  <p className="text-xs text-fg-tertiary font-mono mt-1">{shipment.shipmentId}</p>
                </div>
                <Badge variant="neutral">{shipmentStatusLabel(shipment.status)}</Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-fg-secondary">
                <span>
                  <strong className="font-medium text-fg-primary">Destination</strong>
                  <br />
                  {shipment.destinationFc?.trim() || 'Destination not listed'}
                </span>
                <span>
                  <strong className="font-medium text-fg-primary">Contents</strong>
                  <br />
                  {shipmentSummary(shipment)}
                </span>
              </div>
            </button>
          </Card>
        ))}
        {unassignedShipments.length > 0 && (
          <div className="pt-3 space-y-3" aria-label="Unassigned shipments">
            <h3 className="text-sm font-medium">Needs facility assignment</h3>
            {unassignedShipments.map((shipment) => (
              <Card key={shipment.id}>
                <p className="font-medium">{shipmentLabel(shipment)}</p>
                <p className="text-xs text-fg-tertiary font-mono mt-1">{shipment.shipmentId}</p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={() => handleAssignment(shipment)}
                  disabled={assignment.isPending}
                >
                  {assignment.isPending ? 'Assigning…' : `Assign to ${facilityName}`}
                </Button>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section aria-labelledby="packing-boxes-heading" className="space-y-3">
        <div>
          <h2 id="packing-boxes-heading" className="text-lg font-medium">
            Boxes
          </h2>
          <p className="text-sm text-fg-secondary">Save each box before finalizing it.</p>
        </div>
        {!selectedShipment ? (
          <Card>
            <p className="text-sm text-fg-secondary">Select a shipment to see its boxes.</p>
          </Card>
        ) : (
          <>
            <Card>
              <label htmlFor="new-box-number" className="block text-sm font-medium">
                New box number
              </label>
              <div className="mt-2">
                <div className="flex gap-2">
                  <input
                    id="new-box-number"
                    value={newBoxNumber}
                    onChange={(event) => {
                      setNewBoxNumber(event.target.value);
                      setInvalidNewBoxNumber(false);
                      if (errorMessage === 'Enter a box number before creating a box.') {
                        setErrorMessage(null);
                      }
                    }}
                    aria-invalid={invalidNewBoxNumber}
                    aria-describedby="new-box-number-help"
                    className="min-w-0 flex-1 h-10 px-3 bg-surface border border-border-default rounded-sm text-sm"
                  />
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleCreateBox}
                    disabled={createBox.isPending}
                  >
                    {createBox.isPending ? 'Creating…' : 'Create box'}
                  </Button>
                </div>
                <span id="new-box-number-help" className="block text-xs text-fg-tertiary mt-1">
                  Enter a box number before creating a box.
                </span>
              </div>
            </Card>
            {boxesQuery.isLoading && (
              <div className="space-y-3" aria-hidden="true">
                {Array.from({ length: 2 }, (_, i) => (
                  <Card key={i}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <SkeletonLine className="w-24" />
                        <SkeletonLine className="w-32 mt-2 h-3" />
                      </div>
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                  </Card>
                ))}
              </div>
            )}
            {boxesQuery.isError && (
              <Card>
                <p className="text-sm text-fg-secondary">Could not load boxes.</p>
                <button
                  type="button"
                  onClick={() => void boxesQuery.refetch()}
                  className="mt-3 text-sm text-accent-fg hover:underline"
                >
                  Retry
                </button>
              </Card>
            )}
            {!boxesQuery.isLoading && !boxesQuery.isError && boxes.length === 0 && (
              <Card>
                <p className="text-sm text-fg-secondary">
                  No boxes yet. Create the first box above.
                </p>
              </Card>
            )}
            {boxes.map((box) => (
              <BoxCard
                key={box.id}
                box={box}
                shipment={selectedShipment}
                boxes={boxes}
                open={openBoxId === box.id}
                onOpen={() => setOpenBoxId(openBoxId === box.id ? null : box.id)}
                draftValue={draftValue}
                onDraftChange={setDraftValue}
                onSave={() => handleSaveBox(box)}
                onFinalize={() => requestFinalize(box)}
                saving={replaceContents.isPending}
                finalizing={finalizeBox.isPending && finalizeBoxId === box.id}
                invalidLineKey={invalidLineKey}
                finalizeTriggerRef={(element) => {
                  finalizeTriggerRefs.current[box.id] = element;
                }}
                finalizedHeadingRef={(element) => {
                  finalizedHeadingRefs.current[box.id] = element;
                }}
              />
            ))}
          </>
        )}
      </section>
      {errorMessage && (
        <div id="packing-error" role="alert" className="md:col-span-2 text-sm text-negative">
          {errorMessage}
        </div>
      )}
      <Dialog
        open={Boolean(candidateBox)}
        onOpenChange={(open) => {
          if (!open) {
            closeFinalizeDialog();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalize box {candidateBox?.boxNumber}</DialogTitle>
            <DialogDescription>
              Finalizing locks these contents for warehouse work. You cannot edit this box
              afterward.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={closeFinalizeDialog}
              disabled={finalizeBox.isPending}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleFinalize} disabled={finalizeBox.isPending}>
              {finalizeBox.isPending ? 'Finalizing…' : 'Finalize box'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChargesWorkspace({
  facilityId,
  setAnnouncement,
}: {
  facilityId: string;
  setAnnouncement: (message: string) => void;
}) {
  const servicesQuery = useWarehouseServices(facilityId, true);
  const shipmentsQuery = useWarehouseBillingShipments(facilityId, true);
  const chargesQuery = useWarehouseWorkCharges({ warehouseId: facilityId }, true);
  const [sourceKind, setSourceKind] = useState<ChargeSourceKind>('shipment');
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [selectedBoxId, setSelectedBoxId] = useState<string | null>(null);
  const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState('');
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [localCharges, setLocalCharges] = useState<ChargeResponse[]>([]);
  const [reversing, setReversing] = useState<ChargeResponse | null>(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseError, setReverseError] = useState<string | null>(null);
  const [focusChargeId, setFocusChargeId] = useState<string | null>(null);
  const reverseTriggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const chargeFocusRefs = useRef<Record<string, HTMLSpanElement | null>>({});

  const createCharge = useCreateWarehouseWorkCharge();
  const reverseCharge = useReverseWarehouseWorkCharge();
  const shipments = (shipmentsQuery.data ?? []).filter(
    (shipment) => shipment.warehouseId === facilityId
  );
  const activeServices = (servicesQuery.data ?? []).filter(
    (service) => service.warehouseId === facilityId && service.active
  );
  const unsupportedServices = activeServices.filter(
    (service) => !isWarehouseServiceSupported(service).supported
  );
  const selectedShipment =
    shipments.find((shipment) => shipment.id === selectedShipmentId) ?? shipments[0] ?? null;
  const effectiveShipmentId = selectedShipment?.id ?? null;
  const selectedLine =
    selectedShipment?.lines.find((line) => line.id === selectedLineId) ??
    selectedShipment?.lines[0] ??
    null;
  const effectiveLineId = selectedLine?.id ?? null;
  const boxesQuery = useWarehouseShipmentBoxes(
    facilityId,
    effectiveShipmentId ?? undefined,
    sourceKind === 'box'
  );
  const finalizedBoxes = (boxesQuery.data ?? []).filter(
    (box) => box.warehouseId === facilityId && box.status === 'finalized'
  );
  const selectedBox =
    finalizedBoxes.find((box) => box.id === selectedBoxId) ?? finalizedBoxes[0] ?? null;
  const effectiveBoxId = selectedBox?.id ?? null;
  const selectedService =
    activeServices.find((service) => service.id === selectedServiceId) ?? activeServices[0] ?? null;
  const serviceSupport = selectedService ? isWarehouseServiceSupported(selectedService) : null;
  const boxBasisError =
    selectedService?.billingBasis === 'box' && sourceKind !== 'box'
      ? 'This box-based service must be recorded against a finalized packing box.'
      : null;
  const parsedQuantity = parseWarehouseQuantityMillis(quantity);
  const sourceId =
    sourceKind === 'shipment'
      ? (selectedShipment?.id ?? null)
      : sourceKind === 'shipment_line'
        ? (selectedLine?.id ?? null)
        : (selectedBox?.id ?? null);
  const sourceHealthOk =
    !servicesQuery.isLoading &&
    !servicesQuery.isError &&
    !shipmentsQuery.isLoading &&
    !shipmentsQuery.isError &&
    (sourceKind !== 'box' || (!boxesQuery.isLoading && !boxesQuery.isError));
  const createKey = useWarehouseIdempotencyKey({
    action: 'create',
    serviceId: selectedService?.id ?? null,
    sourceKind,
    sourceId,
    quantityMillis: parsedQuantity.ok ? parsedQuantity.quantityMillis : null,
  });
  const reverseKey = useWarehouseIdempotencyKey({
    action: 'reverse',
    chargeId: reversing?.charge.id ?? null,
    reason: reverseReason.trim(),
  });

  useEffect(() => {
    if (!chargesQuery.data) return;
    const ids = new Set(chargesQuery.data.map((row) => row.charge.id));
    setLocalCharges((current) => {
      const next = current.filter((row) => !ids.has(row.charge.id));
      return next.length === current.length ? current : next;
    });
  }, [chargesQuery.data]);

  const remoteCharges = (chargesQuery.data ?? []).filter(
    (row) => row.charge.warehouseId === facilityId
  );
  const remoteIds = new Set(remoteCharges.map((row) => row.charge.id));
  const chargeRows = [
    ...remoteCharges,
    ...localCharges.filter((row) => !remoteIds.has(row.charge.id)),
  ].sort((a, b) => chargeSortTime(b) - chargeSortTime(a));
  const reversedIds = new Set(
    chargeRows.flatMap((row) =>
      row.charge.reversalOfChargeId ? [row.charge.reversalOfChargeId] : []
    )
  );
  const groups = groupWarehouseCharges(chargeRows);

  useEffect(() => {
    if (!focusChargeId) return;
    const target = chargeFocusRefs.current[focusChargeId];
    if (!target) return;
    target.focus();
    setFocusChargeId(null);
  }, [chargeRows, focusChargeId]);

  function sourcePayload():
    | { sourceShipmentId: string }
    | { sourceShipmentLineId: string }
    | { sourceBoxId: string }
    | null {
    if (!sourceId) return null;
    if (sourceKind === 'shipment') return { sourceShipmentId: sourceId };
    if (sourceKind === 'shipment_line') return { sourceShipmentLineId: sourceId };
    return { sourceBoxId: sourceId };
  }

  function refetchCharges() {
    void chargesQuery.refetch();
  }

  function handleCreateCharge() {
    setMutationError(null);
    setQuantityError(null);
    setSourceError(null);
    if (!sourceHealthOk) return;
    if (!selectedService) {
      setMutationError('Select an active warehouse service first.');
      return;
    }
    if (serviceSupport && !serviceSupport.supported) {
      setMutationError(serviceSupport.reason ?? 'This service is not supported here.');
      return;
    }
    if (boxBasisError) {
      setSourceError(boxBasisError);
      return;
    }
    if (!parsedQuantity.ok) {
      setQuantityError(parsedQuantity.error);
      return;
    }
    const source = sourcePayload();
    if (!source) {
      setSourceError('Select a shipment, shipment item, or finalized packing box.');
      return;
    }
    const payload = {
      serviceId: selectedService.id,
      quantityMillis: parsedQuantity.quantityMillis,
      idempotencyKey: createKey,
      ...source,
    };
    void createCharge
      .mutateAsync(payload)
      .then((result) => {
        setLocalCharges((current) => [result, ...current]);
        setQuantity('');
        setMutationError(null);
        setAnnouncement('Warehouse charge recorded.');
        refetchCharges();
      })
      .catch((error: unknown) => {
        setMutationError(warehouseChargeError(error));
        refetchCharges();
        if (isPersistedBlockedCharge(error)) {
          setAnnouncement(
            'Charge was recorded; it needs allocation attention. Charge history was refreshed.'
          );
        }
      });
  }

  function focusReverseTrigger(chargeId: string | null) {
    if (!chargeId) return;
    queueMicrotask(() => reverseTriggerRefs.current[chargeId]?.focus());
  }

  function closeReverseDialog() {
    const chargeId = reversing?.charge.id ?? null;
    setReversing(null);
    setReverseReason('');
    setReverseError(null);
    focusReverseTrigger(chargeId);
  }

  function handleReverse() {
    if (!reversing) return;
    const reason = reverseReason.trim();
    if (!reason) {
      setReverseError('Enter a reason before reversing this charge.');
      return;
    }
    setReverseError(null);
    void reverseCharge
      .mutateAsync({ chargeId: reversing.charge.id, reason, idempotencyKey: reverseKey })
      .then((result) => {
        setLocalCharges((current) => [result, ...current]);
        const chargeId = reversing.charge.id;
        setReversing(null);
        setReverseReason('');
        setAnnouncement('Charge reversed with an immutable negative entry.');
        refetchCharges();
        setFocusChargeId(chargeId);
      })
      .catch((error: unknown) => {
        setReverseError(warehouseChargeError(error));
        refetchCharges();
      });
  }

  function canReverse(row: ChargeResponse): boolean {
    return (
      !row.charge.reversalOfChargeId &&
      !reversedIds.has(row.charge.id) &&
      row.charge.activeInvoice === null &&
      !reverseCharge.isPending
    );
  }

  function renderChargeDetails(row: ChargeResponse, labeled = false) {
    const charge = row.charge;
    const source = chargeSourceLabel(charge, shipments, finalizedBoxes);
    const service = `${charge.code} · ${charge.description}`;
    const quantityAndRate = `${formatWarehouseQuantityMillis(charge.quantityMillis)} @ ${formatWarehouseCents(charge.rateCents, charge.currency)} ${charge.currency}`;
    const total = `${formatWarehouseCents(charge.amountCents, charge.currency)} ${charge.currency}`;
    const treatmentAndState = `${humanizeWarehouseBasis(charge.billingBasis)} · ${accountingTreatmentLabel(charge.accountingTreatment)} · ${chargeStateLabel(charge, reversedIds)}`;
    const field = (label: string, value: string) =>
      labeled ? (
        <>
          <strong>{label}:</strong> {value}
        </>
      ) : (
        value
      );
    return (
      <>
        <span
          ref={(element) => {
            chargeFocusRefs.current[charge.id] = element;
          }}
          tabIndex={-1}
        >
          {field('Source', source)}
        </span>
        <span>{field('Service', service)}</span>
        <span>{field('Quantity and rate', quantityAndRate)}</span>
        <span>{field('Total', total)}</span>
        <span>{field('Treatment and state', treatmentAndState)}</span>
      </>
    );
  }

  function renderChargeAction(row: ChargeResponse) {
    return canReverse(row) ? (
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={reverseCharge.isPending}
        ref={(element) => {
          reverseTriggerRefs.current[row.charge.id] = element;
        }}
        onClick={() => {
          setReverseError(null);
          setReverseReason('');
          setReversing(row);
        }}
      >
        Reverse
      </Button>
    ) : (
      <span className="text-xs text-fg-tertiary">No action</span>
    );
  }

  function renderGroup(title: string, rows: ChargeResponse[]) {
    return (
      <section
        key={title}
        aria-labelledby={`warehouse-charge-group-${title.toLowerCase().replaceAll(' ', '-')}`}
      >
        <h3
          id={`warehouse-charge-group-${title.toLowerCase().replaceAll(' ', '-')}`}
          className="text-lg font-medium"
        >
          {title}
        </h3>
        {rows.length === 0 ? (
          <p className="text-sm text-fg-tertiary mt-2">No charges in this group.</p>
        ) : (
          <>
            <div role="table" className="hidden md:block mt-3">
              <div
                role="row"
                className="grid grid-cols-6 gap-3 border-b border-border-subtle pb-2 text-xs font-medium text-fg-tertiary"
              >
                <span>Source</span>
                <span>Service</span>
                <span>Quantity and rate</span>
                <span>Total</span>
                <span>Treatment and state</span>
                <span>Action</span>
              </div>
              {rows.map((row) => (
                <div
                  role="row"
                  key={row.charge.id}
                  className="grid grid-cols-6 gap-3 py-3 border-b border-border-subtle text-sm"
                >
                  {renderChargeDetails(row)}
                  <span>{renderChargeAction(row)}</span>
                </div>
              ))}
            </div>
            <div className="md:hidden mt-3 space-y-3">
              {rows.map((row) => (
                <Card key={row.charge.id}>
                  <div className="grid gap-2 text-sm">{renderChargeDetails(row, true)}</div>
                  <div className="mt-3">
                    <span className="font-medium">Action: </span>
                    {renderChargeAction(row)}
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}
      </section>
    );
  }

  return (
    <div className="space-y-6" data-charge-layout="responsive">
      <Card>
        <h2 className="text-lg font-medium">Record warehouse work</h2>
        <p className="text-sm text-fg-secondary mt-1">
          Select an approved service and a facility source. Rates and totals come from the server.
        </p>
        <div className="grid gap-4 md:grid-cols-2 mt-4">
          <div>
            <label htmlFor="warehouse-charge-service" className="block text-sm font-medium">
              Service
            </label>
            {servicesQuery.isLoading ? (
              <Skeleton className="h-10 w-full rounded-sm mt-2" />
            ) : servicesQuery.isError ? (
              <div className="text-sm text-fg-secondary mt-2">
                Could not load services.{' '}
                <button
                  type="button"
                  onClick={() => void servicesQuery.refetch()}
                  className="text-accent-fg hover:underline"
                >
                  Retry
                </button>
              </div>
            ) : activeServices.length === 0 ? (
              <p className="text-sm text-fg-tertiary mt-2">No active services are available.</p>
            ) : (
              <>
                <select
                  id="warehouse-charge-service"
                  value={selectedService?.id ?? ''}
                  onChange={(event) => setSelectedServiceId(event.target.value || null)}
                  className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
                >
                  {activeServices.map((service) => {
                    const support = isWarehouseServiceSupported(service);
                    return (
                      <option key={service.id} value={service.id} disabled={!support.supported}>
                        {service.code} · {service.description}
                        {!support.supported ? ' — unavailable' : ''}
                      </option>
                    );
                  })}
                </select>
                {selectedService && (
                  <p className="text-xs text-fg-tertiary mt-2">
                    {selectedService.code} · {selectedService.description} ·{' '}
                    {formatWarehouseCents(
                      selectedService.defaultRateCents,
                      selectedService.currency
                    )}{' '}
                    {selectedService.currency} ·{' '}
                    {humanizeWarehouseBasis(selectedService.billingBasis)} ·{' '}
                    {accountingTreatmentLabel(selectedService.accountingTreatment)}
                  </p>
                )}
                {unsupportedServices.length > 0 && (
                  <ul
                    aria-label="Unavailable warehouse services"
                    className="text-sm text-negative mt-2 space-y-1"
                  >
                    {unsupportedServices.map((service) => (
                      <li key={service.id}>
                        {service.code}: {isWarehouseServiceSupported(service).reason}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
          <div>
            <label htmlFor="warehouse-charge-quantity" className="block text-sm font-medium">
              Quantity
            </label>
            <input
              id="warehouse-charge-quantity"
              value={quantity}
              inputMode="decimal"
              onChange={(event) => {
                setQuantity(event.target.value);
                setQuantityError(null);
              }}
              aria-invalid={Boolean(quantityError)}
              aria-describedby={quantityError ? 'warehouse-charge-quantity-error' : undefined}
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            />
            {quantityError && (
              <p id="warehouse-charge-quantity-error" className="text-sm text-negative mt-1">
                {quantityError}
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <div>
            <label htmlFor="warehouse-charge-source-kind" className="block text-sm font-medium">
              Source
            </label>
            <select
              id="warehouse-charge-source-kind"
              value={sourceKind}
              onChange={(event) => setSourceKind(event.target.value as ChargeSourceKind)}
              aria-invalid={Boolean(sourceError || boxBasisError)}
              aria-describedby={
                sourceError || boxBasisError ? 'warehouse-charge-source-error' : undefined
              }
              className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
            >
              <option value="shipment">Shipment</option>
              <option value="shipment_line">Shipment item</option>
              <option value="box">Finalized packing box</option>
            </select>
          </div>
          {shipmentsQuery.isLoading ? (
            <div aria-hidden="true">
              <SkeletonLine className="w-16 h-3" />
              <Skeleton className="h-10 w-full rounded-sm mt-2" />
            </div>
          ) : shipmentsQuery.isError ? (
            <div className="text-sm text-fg-secondary">
              Could not load shipments.{' '}
              <button
                type="button"
                onClick={() => void shipmentsQuery.refetch()}
                className="text-accent-fg hover:underline"
              >
                Retry
              </button>
            </div>
          ) : shipments.length === 0 ? (
            <p className="text-sm text-fg-tertiary">No shipments are available at this facility.</p>
          ) : (
            <div>
              <label htmlFor="warehouse-charge-shipment" className="block text-sm font-medium">
                Shipment
              </label>
              <select
                id="warehouse-charge-shipment"
                value={effectiveShipmentId ?? ''}
                onChange={(event) => {
                  setSelectedShipmentId(event.target.value || null);
                  setSelectedLineId(null);
                  setSelectedBoxId(null);
                }}
                className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
              >
                {shipments.map((shipment) => (
                  <option key={shipment.id} value={shipment.id}>
                    {shipmentLabel(shipment)}
                  </option>
                ))}
              </select>
              {sourceKind === 'shipment_line' && (
                <>
                  <label htmlFor="warehouse-charge-line" className="block text-sm font-medium mt-3">
                    Shipment item
                  </label>
                  <select
                    id="warehouse-charge-line"
                    value={effectiveLineId ?? ''}
                    onChange={(event) => setSelectedLineId(event.target.value || null)}
                    className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
                  >
                    {(selectedShipment?.lines ?? []).map((line) => (
                      <option key={line.id} value={line.id}>
                        {line.sellerSku}
                      </option>
                    ))}
                  </select>
                  {selectedLine && (
                    <p className="text-xs text-fg-tertiary mt-1">{selectedLine.sellerSku}</p>
                  )}
                </>
              )}
              {sourceKind === 'box' && (
                <>
                  {boxesQuery.isLoading ? (
                    <div className="mt-3" aria-hidden="true">
                      <SkeletonLine className="w-32 h-3" />
                      <Skeleton className="h-10 w-full rounded-sm mt-2" />
                    </div>
                  ) : boxesQuery.isError ? (
                    <div className="text-sm text-fg-secondary mt-3">
                      Could not load packing boxes.{' '}
                      <button
                        type="button"
                        onClick={() => void boxesQuery.refetch()}
                        className="text-accent-fg hover:underline"
                      >
                        Retry
                      </button>
                    </div>
                  ) : finalizedBoxes.length === 0 ? (
                    <p className="text-sm text-fg-tertiary mt-3">
                      No finalized packing boxes are available.
                    </p>
                  ) : (
                    <>
                      <label
                        htmlFor="warehouse-charge-box"
                        className="block text-sm font-medium mt-3"
                      >
                        Finalized packing box
                      </label>
                      <select
                        id="warehouse-charge-box"
                        value={effectiveBoxId ?? ''}
                        onChange={(event) => setSelectedBoxId(event.target.value || null)}
                        className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
                      >
                        {finalizedBoxes.map((box) => (
                          <option key={box.id} value={box.id}>
                            Box {box.boxNumber}
                          </option>
                        ))}
                      </select>
                      {selectedBox && (
                        <p className="text-xs text-fg-tertiary mt-1">Finalized packing box</p>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {(sourceError || boxBasisError) && (
          <p id="warehouse-charge-source-error" className="text-sm text-negative mt-2">
            {sourceError ?? boxBasisError}
          </p>
        )}
        <Button
          type="button"
          className="mt-4"
          onClick={handleCreateCharge}
          disabled={
            createCharge.isPending ||
            !selectedService ||
            !sourceHealthOk ||
            Boolean(serviceSupport && !serviceSupport.supported) ||
            Boolean(boxBasisError) ||
            !sourceId
          }
        >
          {createCharge.isPending ? 'Recording…' : 'Record charge'}
        </Button>
      </Card>

      <section aria-labelledby="warehouse-charge-history-heading" className="space-y-5">
        <div>
          <h2 id="warehouse-charge-history-heading" className="text-lg font-medium">
            Charge history
          </h2>
          {chargesQuery.isLoading && (
            <div aria-hidden="true">
              <div role="table" className="hidden md:block mt-3">
                <div
                  role="row"
                  className="grid grid-cols-6 gap-3 border-b border-border-subtle pb-2 text-xs font-medium text-fg-tertiary"
                >
                  <span>Source</span>
                  <span>Service</span>
                  <span>Quantity and rate</span>
                  <span>Total</span>
                  <span>Treatment and state</span>
                  <span>Action</span>
                </div>
                {Array.from({ length: 3 }, (_, i) => (
                  <div
                    role="row"
                    key={i}
                    className="grid grid-cols-6 gap-3 py-3 border-b border-border-subtle"
                  >
                    {Array.from({ length: 6 }, (_, j) => (
                      <SkeletonLine key={j} className="w-3/4" />
                    ))}
                  </div>
                ))}
              </div>
              <div className="md:hidden mt-3 space-y-3">
                {Array.from({ length: 2 }, (_, i) => (
                  <Card key={i}>
                    <div className="grid gap-2">
                      <SkeletonLine className="w-2/3" />
                      <SkeletonLine className="w-1/2" />
                      <SkeletonLine className="w-1/3" />
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}
          {chargesQuery.isError && (
            <div className="text-sm text-fg-secondary mt-2">
              Could not load charge history.{' '}
              <button
                type="button"
                onClick={refetchCharges}
                className="text-accent-fg hover:underline"
              >
                Retry
              </button>
            </div>
          )}
        </div>
        {!chargesQuery.isLoading && !chargesQuery.isError && chargeRows.length === 0 && (
          <p className="text-sm text-fg-tertiary">No warehouse charges yet.</p>
        )}
        {!chargesQuery.isLoading &&
          !chargesQuery.isError &&
          renderGroup('Unbilled', groups.unbilled)}
        {!chargesQuery.isLoading &&
          !chargesQuery.isError &&
          renderGroup('Reserved in draft', groups.draft)}
        {!chargesQuery.isLoading &&
          !chargesQuery.isError &&
          renderGroup('Submitted / needs changes', groups.sent)}
        {!chargesQuery.isLoading &&
          !chargesQuery.isError &&
          renderGroup('Completed history', groups.completed)}
      </section>

      <div id="warehouse-charge-error" role="alert" className="text-sm text-negative min-h-5">
        {mutationError ?? reverseError}
      </div>

      <Dialog open={Boolean(reversing)} onOpenChange={(open) => !open && closeReverseDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reverse charge</DialogTitle>
            <DialogDescription>
              This creates an immutable negative entry for{' '}
              {reversing ? chargeSourceLabel(reversing.charge, shipments, finalizedBoxes) : ''} ({' '}
              {reversing
                ? formatWarehouseCents(reversing.charge.amountCents, reversing.charge.currency)
                : ''}
              ).
            </DialogDescription>
          </DialogHeader>
          <label htmlFor="warehouse-reverse-reason" className="block text-sm font-medium mt-3">
            Reason
          </label>
          <input
            id="warehouse-reverse-reason"
            value={reverseReason}
            onChange={(event) => {
              setReverseReason(event.target.value);
              setReverseError(null);
            }}
            aria-invalid={Boolean(reverseError)}
            aria-describedby={reverseError ? 'warehouse-reverse-reason-error' : undefined}
            className="w-full h-10 px-3 mt-2 bg-surface border border-border-default rounded-sm text-sm"
          />
          {reverseError && (
            <p id="warehouse-reverse-reason-error" className="text-sm text-negative mt-1">
              {reverseError}
            </p>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={closeReverseDialog}
              disabled={reverseCharge.isPending}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleReverse} disabled={reverseCharge.isPending}>
              {reverseCharge.isPending ? 'Reversing…' : 'Reverse charge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BoxCard({
  box,
  shipment,
  boxes,
  open,
  onOpen,
  draftValue,
  onDraftChange,
  onSave,
  onFinalize,
  saving,
  finalizing,
  invalidLineKey,
  finalizeTriggerRef,
  finalizedHeadingRef,
}: {
  box: PackingBox;
  shipment: WarehouseShipment;
  boxes: PackingBox[];
  open: boolean;
  onOpen: () => void;
  draftValue: (box: PackingBox, line: PackingLine) => string;
  onDraftChange: (boxId: string, lineId: string, value: string) => void;
  onSave: () => void;
  onFinalize: () => void;
  saving: boolean;
  finalizing: boolean;
  invalidLineKey: string | null;
  finalizeTriggerRef: (element: HTMLButtonElement | null) => void;
  finalizedHeadingRef: (element: HTMLHeadingElement | null) => void;
}) {
  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3
            ref={box.status === 'finalized' ? finalizedHeadingRef : undefined}
            tabIndex={box.status === 'finalized' ? -1 : undefined}
            className="font-medium"
          >
            Box {box.boxNumber}
          </h3>
          <p className="text-xs text-fg-tertiary mt-1">
            {box.status === 'finalized'
              ? 'Finalized — contents are read-only.'
              : 'Open — contents can be edited.'}
          </p>
        </div>
        <Badge variant={box.status === 'finalized' ? 'positive' : 'neutral'}>
          {box.status === 'finalized' ? 'Finalized' : 'Open'}
        </Badge>
      </div>
      {box.status === 'finalized' ? (
        <>
          {box.finalizedAt && (
            <p className="text-xs text-fg-tertiary mt-2">
              Finalized on {finalizedAtLabel(box.finalizedAt)}
            </p>
          )}
          <ul className="mt-4 space-y-2 text-sm">
            {box.contents.map((content) => (
              <li key={content.id} className="flex justify-between gap-3">
                <span>
                  <span className="font-mono">{content.sellerSku}</span>
                </span>
                <span>{content.listingUnits} listing units</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={onOpen}
            aria-expanded={open}
            className="mt-4 text-sm text-accent-fg hover:underline"
          >
            {open ? 'Hide contents' : 'Edit contents'}
          </button>
          {open && (
            <div className="mt-3 space-y-3">
              {shipment.lines.map((line) => {
                const max = maxReplacementUnits(line, boxes, box, line.id);
                const remaining = remainingShipmentUnits(line, boxes);
                const invalidId = `box-${box.id}-line-${line.id}-error`;
                return (
                  <div
                    key={line.id}
                    className="grid grid-cols-[minmax(0,1fr)_6rem] gap-3 items-start"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-mono truncate">{line.sellerSku}</p>
                      <p className="text-xs text-fg-tertiary">
                        {line.unitsPerListingSnapshot === null
                          ? 'Pack-size mapping missing.'
                          : `${remaining} remaining to pack`}
                      </p>
                    </div>
                    <div>
                      <label htmlFor={`box-${box.id}-line-${line.id}`} className="sr-only">
                        {line.sellerSku} listing units
                      </label>
                      <input
                        id={`box-${box.id}-line-${line.id}`}
                        inputMode="numeric"
                        value={draftValue(box, line)}
                        onChange={(event) => onDraftChange(box.id, line.id, event.target.value)}
                        aria-describedby={
                          invalidLineKey === `${box.id}:${line.id}`
                            ? `${invalidId} packing-error`
                            : invalidId
                        }
                        aria-invalid={invalidLineKey === `${box.id}:${line.id}`}
                        className="w-full h-9 px-2 bg-surface border border-border-default rounded-sm text-sm text-right"
                      />
                      <span
                        id={invalidId}
                        className="block text-[10px] text-fg-tertiary mt-1 text-right"
                      >
                        max {max}
                      </span>
                    </div>
                  </div>
                );
              })}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={onSave}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save contents'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={onFinalize}
                  disabled={finalizing}
                  ref={finalizeTriggerRef}
                >
                  {finalizing ? 'Finalizing…' : 'Finalize box'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

export function WarehouseBillingPage({ section }: { section: WarehouseBillingSection }) {
  const { data: me, isLoading: meLoading, isError: meError, refetch: refetchMe } = useMe();
  const warehouseEnabled = useModuleEnabled('warehouse');
  const role = me?.user.role as string | undefined;
  const canRequestFacilities = Boolean(me && hasWarehouseRole(role) && warehouseEnabled);
  const facilitiesQuery = useWarehouseFacilities(canRequestFacilities);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState(`${SECTION_COPY[section].title} ready.`);
  const copy = SECTION_COPY[section];

  if (meError && !meLoading && !me) {
    return (
      <PageFrame announcement="Warehouse access check failed.">
        <Card>
          <h1 className="text-xl font-medium">Could not check warehouse access</h1>
          <p className="text-sm text-fg-secondary mt-2">
            Try again to check your session before continuing warehouse work.
          </p>
          <button
            type="button"
            onClick={() => void refetchMe()}
            className="mt-4 text-sm text-accent-fg hover:underline"
          >
            Retry
          </button>
        </Card>
      </PageFrame>
    );
  }

  if (meLoading || !me) {
    return (
      <PageFrame announcement="Checking warehouse access.">
        <p className="text-sm text-fg-tertiary">Checking warehouse access…</p>
      </PageFrame>
    );
  }

  if (role === 'va' || !hasWarehouseRole(role)) {
    return (
      <PageFrame>
        <Card>
          <h1 className="text-xl font-medium">Warehouse work unavailable</h1>
          <p className="text-sm text-fg-secondary mt-2">
            This account is not granted access to warehouse work.
          </p>
        </Card>
      </PageFrame>
    );
  }

  if (!warehouseEnabled) {
    return (
      <PageFrame>
        <Card>
          <h1 className="text-xl font-medium">Warehouse module unavailable</h1>
          <p className="text-sm text-fg-secondary mt-2">
            Warehouse work is not enabled for this workspace.
          </p>
        </Card>
      </PageFrame>
    );
  }

  if (facilitiesQuery.isLoading) {
    return (
      <PageFrame announcement="Loading warehouse facilities.">
        <p className="text-sm text-fg-tertiary">Loading warehouse facilities…</p>
      </PageFrame>
    );
  }

  if (facilitiesQuery.isError) {
    if (isUnassignedWarehouseError(facilitiesQuery.error)) {
      return (
        <PageFrame>
          <Card>
            <h1 className="text-xl font-medium">Warehouse facility not assigned</h1>
            <p className="text-sm text-fg-secondary mt-2">
              Ask an owner or operator to assign this warehouse account to a facility.
            </p>
          </Card>
        </PageFrame>
      );
    }
    return (
      <PageFrame>
        <Card>
          <h1 className="text-xl font-medium">Could not load warehouse facilities</h1>
          <p className="text-sm text-fg-secondary mt-2">
            Try again before continuing warehouse work.
          </p>
          <button
            type="button"
            onClick={() => void facilitiesQuery.refetch()}
            className="mt-4 text-sm text-accent-fg hover:underline"
          >
            Retry
          </button>
        </Card>
      </PageFrame>
    );
  }

  const facilities = facilitiesQuery.data ?? [];
  if (facilities.length === 0) {
    return (
      <PageFrame>
        <Card>
          <h1 className="text-xl font-medium">No warehouse facilities</h1>
          <p className="text-sm text-fg-secondary mt-2">
            Add a facility before starting warehouse work.
          </p>
        </Card>
      </PageFrame>
    );
  }

  const normalizedSelectedFacilityId = selectedFacilityId
    ? facilities.some((facility) => facility.id === selectedFacilityId)
      ? selectedFacilityId
      : null
    : null;
  const selectedFacility = resolveWarehouseFacility(facilities, normalizedSelectedFacilityId);
  return (
    <PageFrame announcement={announcement}>
      <header className="border-b border-border-subtle pb-6">
        <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">WAREHOUSE WORK</p>
        <h1 className="font-display text-4xl sm:text-[44px] font-medium leading-[1.08] tracking-tight mt-3">
          {copy.title}
        </h1>
        <p className="text-sm text-fg-secondary mt-3 max-w-2xl">{copy.description}</p>
      </header>
      {facilities.length > 1 && (
        <div className="max-w-sm">
          <label htmlFor="warehouse-facility" className="block text-sm font-medium mb-2">
            Facility
          </label>
          <select
            id="warehouse-facility"
            value={normalizedSelectedFacilityId ?? ''}
            onChange={(event) => setSelectedFacilityId(event.target.value || null)}
            className="w-full h-10 px-3 bg-surface border border-border-default rounded-sm text-sm"
          >
            <option value="">Select a facility</option>
            {facilities.map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>
        </div>
      )}
      {selectedFacility ? (
        section === 'packing' ? (
          <>
            <Card>
              <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">
                Selected facility
              </p>
              <h2 className="text-xl font-medium mt-2">{selectedFacility.name}</h2>
            </Card>
            <PackingWorkspace
              key={selectedFacility.id}
              facilityId={selectedFacility.id}
              facilityName={selectedFacility.name}
              warehouseRole={role === 'warehouse'}
              setAnnouncement={setAnnouncement}
            />
          </>
        ) : section === 'charges' ? (
          <>
            <Card>
              <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">
                Selected facility
              </p>
              <h2 className="text-xl font-medium mt-2">{selectedFacility.name}</h2>
            </Card>
            <ChargesWorkspace
              key={selectedFacility.id}
              facilityId={selectedFacility.id}
              setAnnouncement={setAnnouncement}
            />
          </>
        ) : (
          <>
            <Card>
              <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">
                Selected facility
              </p>
              <h2 className="text-xl font-medium mt-2">{selectedFacility.name}</h2>
            </Card>
            <InvoicesWorkspace
              key={selectedFacility.id}
              facilityId={selectedFacility.id}
              facilityName={selectedFacility.name}
              canManageInvoiceLifecycle={role === 'owner' || role === 'operator'}
              setAnnouncement={setAnnouncement}
            />
          </>
        )
      ) : facilities.length > 1 ? (
        <Card>
          <p className="text-sm text-fg-secondary">Select a facility to continue.</p>
        </Card>
      ) : null}
    </PageFrame>
  );
}
